import React, { useState, useEffect } from 'react';
import { FileText, Printer, Search, FileSpreadsheet, History, XCircle, Globe, DollarSign, Building, Landmark, Calendar, Filter, ShoppingCart, Plus, CheckCircle, Clock, Package, Edit2, Trash2, Save, X, RefreshCw, HelpCircle, Calculator, ExternalLink, TrendingUp, Info, Eye, Loader2 } from 'lucide-react';
import api, { cotizacionesService, comprasPendientesService, facturasService, proveedoresService, clientesService, islrConceptosService, validacionesPagoService, conciliacionBancariaService, configService } from '../../../services/api';
import useModal from '../../../hooks/useModal';
import FacturaPrintModal from './FacturaPrintModal';
import NotaCompraPrintModal from './NotaCompraPrintModal';
import ComprobanteRetencionIVAPrintModal from './ComprobanteRetencionIVAPrintModal';
import ComprobanteISLRPrintModal from './ComprobanteISLRPrintModal';

// Componente: Tabla Comparativa Mixta para Anticipos
// Los montos de recaudos vienen ya congelados en su propia moneda (la de
// la cotizacion real): nunca se reconvierten aqui, solo se etiquetan.
const fmtMonto = (valor, moneda = 'USD') => {
  const num = parseFloat(valor);
  const seguro = Number.isFinite(num) ? num : 0;
  return moneda === 'VES'
    ? `Bs. ${seguro.toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : `$${seguro.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const AnticipoMixedTable = ({ anticipoData, totalFactura, tasaActual }) => {
  if (!anticipoData || !anticipoData.requiere_anticipo) return null;

  const anticipoMonto = anticipoData.anticipo_monto_usd || 0;
  const tasaHistorica = anticipoData.anticipo_tasa_cambio || 0;
  const anticipoBs = anticipoMonto * tasaHistorica;
  
  const totalUSD = totalFactura || 0;
  const restanteUSD = totalUSD - anticipoMonto;
  const restanteBs = restanteUSD * tasaActual;
  const totalBs = anticipoBs + restanteBs;

  return (
    <div className="mt-4 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border-2 border-purple-200">
      <div className="flex items-center gap-2 mb-3">
        <TrendingUp size={18} className="text-purple-600" />
        <h4 className="font-bold text-purple-800">Tabla Comparativa Mixta (Anticipo)</h4>
        <Info size={14} className="text-purple-500 ml-auto" title="Primera parte con tasa histórica, segunda con tasa actual" />
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-purple-200">
              <th className="text-left py-2 px-3 font-semibold text-purple-700">Concepto</th>
              <th className="text-right py-2 px-3 font-semibold text-purple-700">Monto USD</th>
              <th className="text-right py-2 px-3 font-semibold text-purple-700">Tasa Cambio</th>
              <th className="text-right py-2 px-3 font-semibold text-purple-700">Equivalente Bs</th>
            </tr>
          </thead>
          <tbody>
            <tr className="bg-purple-100/50">
              <td className="py-2 px-3 font-medium text-purple-900">
                <div className="flex items-center gap-2">
                  <Clock size={12} className="text-purple-600" />
                  Anticipo (Histórico)
                </div>
              </td>
              <td className="text-right py-2 px-3 font-bold text-purple-900">${anticipoMonto.toFixed(2)}</td>
              <td className="text-right py-2 px-3 font-semibold text-purple-700">{tasaHistorica.toFixed(2)}</td>
              <td className="text-right py-2 px-3 font-bold text-purple-900">Bs. {anticipoBs.toFixed(2)}</td>
            </tr>
            <tr className="bg-indigo-100/50">
              <td className="py-2 px-3 font-medium text-indigo-900">
                <div className="flex items-center gap-2">
                  <RefreshCw size={12} className="text-indigo-600" />
                  Diferencial (Actual)
                </div>
              </td>
              <td className="text-right py-2 px-3 font-bold text-indigo-900">${restanteUSD.toFixed(2)}</td>
              <td className="text-right py-2 px-3 font-semibold text-indigo-700">{tasaActual.toFixed(2)}</td>
              <td className="text-right py-2 px-3 font-bold text-indigo-900">Bs. {restanteBs.toFixed(2)}</td>
            </tr>
            <tr className="bg-purple-200/80 font-bold">
              <td className="py-2 px-3 text-purple-900">TOTAL</td>
              <td className="text-right py-2 px-3 text-purple-900">${totalUSD.toFixed(2)}</td>
              <td className="text-right py-2 px-3 text-purple-700">—</td>
              <td className="text-right py-2 px-3 text-purple-900">Bs. {totalBs.toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
      </div>
      
      <div className="mt-3 pt-3 border-t border-purple-200 text-xs text-purple-600">
        <p>
          <Info size={10} className="inline mr-1" />
          El anticipo se calcula con la tasa histórica ({tasaHistorica.toFixed(2)}) congelada al momento del pago.
          El diferencial se calcula con la tasa actual ({tasaActual.toFixed(2)}).
        </p>
      </div>
    </div>
  );
};

const FacturasView = () => {
  const { notify, confirm, ModalRenderer } = useModal();
  const [buscandoTasa, setBuscandoTasa] = useState(false);
  const [facturas, setFacturas] = useState([]);
  const [facturasReales, setFacturasReales] = useState([]);
  const [islrConceptosReales, setIslrConceptosReales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [imprimirId, setImprimirId] = useState(null);
  const [imprimirNotaCompra, setImprimirNotaCompra] = useState(null); // Compra para imprimir nota
  const [activeTab, setActiveTab] = useState('historial'); // 'historial' | 'rechazadas' | 'recaudos'
  const [recaudosFechaInicio, setRecaudosFechaInicio] = useState('');
  const [recaudosFechaFin, setRecaudosFechaFin] = useState('');
  const [recaudosData, setRecaudosData] = useState([]);
  const [recaudosTotalesPorMoneda, setRecaudosTotalesPorMoneda] = useState({});
  const [recaudosTotalesVes, setRecaudosTotalesVes] = useState(null);
  const [loadingRecaudos, setLoadingRecaudos] = useState(false);
  const [proveedores, setProveedores] = useState([]); // Para obtener datos de retención por cliente
  const [clientesData, setClientesData] = useState([]); // Para obtener datos de retención de clientes
  const [updatingId, setUpdatingId] = useState(null);
  const [updatingLlevaFactura, setUpdatingLlevaFactura] = useState(null);
  const [modalEstado, setModalEstado] = useState(null); // { facturaId, estadoActual }
  const [modalISLR, setModalISLR] = useState(null); // { facturaId, articuloIndex, articuloActual, tipoPersona }
  const [tasaCambioActual, setTasaCambioActual] = useState(36.5); // Tasa de cambio actual (default)
  const [modalAnticipo, setModalAnticipo] = useState(null); // { factura }
  const [modalVistaPreviaRetIVA, setModalVistaPreviaRetIVA] = useState(null);
  const [modalISLRComprobante, setModalISLRComprobante] = useState(null); // { factura }
  const [cotizacionDetalle, setCotizacionDetalle] = useState(null);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);
  
  // Estados para Validación de Pago
  const [modalValidacionPago, setModalValidacionPago] = useState(null); // { cotizacionId, cotizacionNumero }
  const [tabValidacionPago, setTabValidacionPago] = useState('pago'); // 'pago' | 'iva' | 'islr'
  // { [cotizacionId|facturaId]: {...} } -- ahora viene de verdad del backend
  // (GET /validaciones-pago), no se pierde al refrescar la pagina.
  const [validacionesPago, setValidacionesPago] = useState({});
  const [cuentasBancarias, setCuentasBancarias] = useState([]); // cuentas propias de Gaman
  const [guardandoValidacion, setGuardandoValidacion] = useState(false);
  // Factura real (facturas.id) ya emitida para la cotizacion abierta, si
  // existe -- sin ella no se pueden registrar retenciones (regla R1: sin
  // factura emitida no hay base de IVA/ISLR sobre la cual retener).
  const [facturaResuelta, setFacturaResuelta] = useState(null);
  const [formValidacion, setFormValidacion] = useState({
    cliente_nombre: '',
    cliente_agente_retencion: false,
    cuenta_bancaria_id: '',
    requiere_factura: false,
    requiere_anticipo: false,
    anticipo_monto_usd: '',
    anticipo_tasa_cambio: '',
    referencia: '',
    fecha: '',
    monto: '',
    // A4: en que moneda pago el cliente y a que tasa -- sin esto la
    // conciliacion no podia convertir y comparaba montos crudos (Bs 16,66
    // emparejado con $16.66 sin pasar por la tasa).
    moneda: 'USD',
    tasa_cambio: '',
    captura: null, // File
    // Campos de retención IVA
    esAgenteRetencionIVA: false,
    numeroRetencionIVA: '',
    fechaRetencionIVA: '',
    montoRetencionIVA: '',
    capturaRetencionIVA: null, // File opcional
    // Campos de ISLR para servicios
    esServicioConISLR: false,
    numeroRetencionISLR: '',
    fechaRetencionISLR: '',
    montoRetencionISLR: '',
    capturaRetencionISLR: null // File opcional
  });
  
  // Estados para Compras Pendientes
  const [comprasPendientes, setComprasPendientes] = useState([]);
  const [modalCompra, setModalCompra] = useState(null); // null | 'nueva' | 'editar'
  const [filtroEstadoCompra, setFiltroEstadoCompra] = useState('todos'); // 'todos' | 'pendiente' | 'en_cotizacion' | 'cotizado' | 'comprado' | 'nota_entrega_lista'
  const [filtroTipoCompra, setFiltroTipoCompra] = useState('todos'); // 'todos' | 'nacional' | 'internacional'
  const [searchCompra, setSearchCompra] = useState('');
  const [modalProveedores, setModalProveedores] = useState(false); // Modal de sugerencia de proveedores
  const [listaProveedores, setListaProveedores] = useState([]); // Lista de proveedores
  
  const [formCompra, setFormCompra] = useState({
    id: null,
    producto: '',
    cantidad: 1,
    descripcion: '',
    tipo: 'nacional', // 'nacional' | 'internacional'
    proveedor_sugerido: '',
    cotizacion_origen: '',
    cliente_nombre: '', // Nombre del cliente/empresa desde cotización
    estado: 'pendiente', // 'pendiente' | 'en_cotizacion' | 'cotizado' | 'comprado' | 'nota_entrega_lista'
    prioridad: 'media', // 'baja' | 'media' | 'alta'
    notas: '',
    tracking_comentario: '', // Comentario de tracking/envío
    factura: '', // Número de factura para la nota de entrega
    // Campos de factura del proveedor
    factura_file: null, // Archivo de factura (imagen/PDF)
    factura_fecha: '', // Fecha de la factura
    factura_numero_proveedor: '', // Número de factura del proveedor
    base_imponible_usd: '', // Base imponible en dólares
    base_imponible_ves: '', // Base imponible en bolívares
    retencion_monto: '', // Monto de retención (si aplica)
    receiver_name: '', // Nombre del receptor (cliente)
    receiver_id: '', // C.I. del receptor
    deliverer_name: '', // Nombre del entregador
    deliverer_id: '', // C.I. del entregador
    fecha_creacion: null,
    // Nuevos campos para el flujo de pago
    proveedor_vende: null, // null | true | false - ¿El proveedor venderá?
    requiere_factura: null, // null | true | false - ¿Requiere factura?
    monto_a_pagar: '', // Monto a pagar
    porcentaje_retencion: '', // Porcentaje de retención (manual)
    monto_retencion_calculado: '', // Monto de retención calculado
    porcentaje_islr: '', // Porcentaje ISLR (manual)
    monto_islr_calculado: '', // Monto ISLR calculado
    monto_transferencia: '', // Monto de transferencia
    referencia_bancaria: '', // Referencia bancaria
    fecha_pago: '', // Fecha de pago
    comprobante_pago: null // Comprobante de pago (archivo)
  });
  
  // Estados para Paginación
  const [paginaFacturas, setPaginaFacturas] = useState(1);
  const [paginaCompras, setPaginaCompras] = useState(1);
  const [paginaRecaudos, setPaginaRecaudos] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      try {
        setLoading(true);
        // Cargar cotizaciones
        const responseCotizaciones = await cotizacionesService.getAll();
        if(isMounted) {
            const data = responseCotizaciones.data?.data || responseCotizaciones.data || [];
            console.log('📊 Datos recibidos del backend:', data);
            if (Array.isArray(data)) {
                // Debug: verificar estados
                data.forEach(f => {
                  console.log(`Factura ${f.id} - Estado recibido: "${f.estado}"`);
                });
                setFacturas(data);
            } else {
                setFacturas([]);
            }
        }
        const responseFacturas = await facturasService.getAll();
        if (isMounted) {
          const facturasData = responseFacturas.data?.data || responseFacturas.data || [];
          setFacturasReales(Array.isArray(facturasData) ? facturasData : []);
        }
        // Cargar proveedores para obtener datos de retención
        const responseProveedores = await proveedoresService.getAll();
        if(isMounted) {
            const provData = responseProveedores.data?.data || responseProveedores.data || [];
            setProveedores(Array.isArray(provData) ? provData : []);
        }
        // Cargar clientes para obtener datos de retención
        const responseClientes = await clientesService.getAll();
        if(isMounted) {
            const clientesRespData = responseClientes.data?.data || responseClientes.data || [];
            setClientesData(Array.isArray(clientesRespData) ? clientesRespData : []);
        }
          const responseCuentas = await conciliacionBancariaService.getCuentas(true);
          if (isMounted) setCuentasBancarias(responseCuentas.data || []);
        // Conceptos ISLR reales (islr_conceptos), no la lista fija que
        // habia antes en el codigo -- esa no tenia condominio, no
        // distinguia domiciliado, y mandaba el porcentaje como entero
        // (3, 5...) en vez de fraccion (0.03, 0.05...).
        const responseIslr = await islrConceptosService.getAll();
        if (isMounted) {
          const islrData = responseIslr.data || [];
          setIslrConceptosReales(Array.isArray(islrData) ? islrData : []);
        }
        // Validaciones de pago reales (antes vivian solo en memoria) y
        // cuentas propias de Gaman para el selector del modal.
        const responseValidaciones = await validacionesPagoService.getAll();
        if (isMounted) {
          setValidacionesPago(responseValidaciones.data || {});
        }
      } catch (error) {
        console.error('Error al cargar datos:', error);
      } finally {
        if(isMounted) {
            setLoading(false);
        }
      }
    };

    fetchData();
    return () => { isMounted = false; };
  }, []);

  // Recaudos IVA/ISLR: viene de /api/recaudos (backend), no de un
  // recalculo propio en el frontend -- ese calculo duplicado tenia IVA
  // fijo al 16%, dividia por 100 de mas la retencion y el ISLR (ya
  // vienen en fraccion), y mostraba todo como pesos mexicanos sin mirar
  // si la cotizacion real estaba en Bs o en USD.
  const cargarRecaudos = async () => {
    setLoadingRecaudos(true);
    try {
      const params = { pasada_a_factura: 'true', lleva_factura: 'true' };
      if (recaudosFechaInicio) params.fecha_inicio = recaudosFechaInicio;
      if (recaudosFechaFin) params.fecha_fin = recaudosFechaFin;
      const res = await islrConceptosService.getRecaudos(params);
      setRecaudosData(res.data?.cotizaciones || []);
      setRecaudosTotalesPorMoneda(res.data?.totales_por_moneda || {});
      setRecaudosTotalesVes(res.data?.totales_ves || null);
    } catch (error) {
      console.error('Error al cargar recaudos:', error);
      setRecaudosData([]);
      setRecaudosTotalesPorMoneda({});
      setRecaudosTotalesVes(null);
    } finally {
      setLoadingRecaudos(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'recaudos') {
      cargarRecaudos();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, recaudosFechaInicio, recaudosFechaFin]);

  // Función para obtener el estado a mostrar en facturas
  const getEstadoFactura = (factura) => {
    // Usar directamente el estado del backend si existe
    const estadoBackend = factura.estado || 'borrador';
    
    // DEBUG
    if (factura.id && factura.estado) {
      console.log(`getEstadoFactura - ID: ${factura.id}, estadoBackend: "${estadoBackend}"`);
    }
    
    // Si el backend tiene estado válido (que NO sea undefined, null, o 'borrador'), usarlo
    if (estadoBackend && estadoBackend !== 'borrador') {
      return estadoBackend;
    }
    
    // FALLBACK: Calcular completitud localmente si el backend no tiene estado
    const articulos = factura.articulos || [];
    if (articulos.length === 0) return 'borrador';
    
    let puntos = 0;
    let maxPuntos = articulos.length * 5;
    
    articulos.forEach(art => {
      if (art.precio_unitario > 0) puntos++;
      if (art.costo_envio >= 0) puntos++;
      if (art.porcentaje_utilidad >= 0) puntos++;
      if (art.tiene_stock !== null && art.tiene_stock !== undefined) puntos++;
      if (art.link_proveedor && art.link_proveedor.trim() !== '') puntos++;
    });
    
    const completitud = Math.round((puntos / maxPuntos) * 100);
    
    // Si está 100% completa, mostrar "En Espera de Pago"
    if (completitud === 100) {
      return 'en_espera_pago';
    }
    
    return estadoBackend;
  };

  const getEstadoFacturacion = (cotizacion) => {
    const facturaReal = facturasReales.find((factura) => (
      String(factura.cotizacion_id || factura.cotizacion_ref) === String(cotizacion.id)
      && factura.estado !== 'anulada'
    ));

    if (facturaReal?.estado === 'pagada') return { clave: 'cobrada', etiqueta: 'Cobrada', clase: 'bg-emerald-100 text-emerald-700 border-emerald-300' };
    if (facturaReal) return { clave: 'con_factura', etiqueta: 'Con factura', clase: 'bg-green-100 text-green-700 border-green-300' };
    if (cotizacion.lleva_factura === true || cotizacion.lleva_factura === 1) {
      return { clave: 'con_factura', etiqueta: 'Con factura', clase: 'bg-green-100 text-green-700 border-green-300' };
    }
    if (cotizacion.lleva_factura === false || cotizacion.lleva_factura === 0) {
      return { clave: 'sin_factura', etiqueta: 'Sin factura', clase: 'bg-gray-100 text-gray-600 border-gray-300' };
    }
    return { clave: 'sin_estatus', etiqueta: 'Sin estatus', clase: 'bg-slate-100 text-slate-500 border-slate-300' };
  };

  const getResumenCobro = (cotizacion) => {
    const facturaReal = facturasReales.find((factura) => (
      String(factura.cotizacion_id || factura.cotizacion_ref) === String(cotizacion.id)
      && factura.estado !== 'anulada'
    ));
    if (!facturaReal) return null;

    const moneda = facturaReal.moneda || cotizacion.moneda || 'USD';
    const total = Number(facturaReal.total) || 0;
    const tasa = Number(facturaReal.tasa) || 0;
    const equivalenteVes = moneda === 'VES' ? total : (tasa > 0 ? total * tasa : null);
    return {
      factura: facturaReal,
      moneda,
      total,
      tasa,
      equivalenteVes
    };
  };

  const abrirDetalleCotizacion = async (cotizacion) => {
    setCargandoDetalle(true);
    try {
      const response = await cotizacionesService.getById(cotizacion.id);
      setCotizacionDetalle(response.data?.data || response.data || response);
    } catch (error) {
      await notify('error', 'Atención', error.message || 'No se pudo cargar el detalle de la cotización');
    } finally {
      setCargandoDetalle(false);
    }
  };

  const descargarFacturaPdf = async (factura) => {
    try {
      const blob = await facturasService.descargarPdf(factura.id);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
      await notify('error', 'Atención', error.message || 'No se pudo generar el PDF de la factura');
    }
  };

  // Función para cambiar estado manualmente
  const handleCambiarEstado = async (facturaId, nuevoEstado) => {
    console.log(`🔄 Cambiando estado de factura ${facturaId} a "${nuevoEstado}"`);
    try {
      setUpdatingId(facturaId);
      const response = await cotizacionesService.actualizarEstado(facturaId, nuevoEstado);
      console.log('✅ Respuesta del backend al cambiar estado:', response);

      // Actualizar la lista local
      setFacturas(prev => prev.map(f =>
        f.id === facturaId ? { ...f, estado: nuevoEstado } : f
      ));

      setModalEstado(null);
      await notify('success', 'Listo', 'Estado actualizado correctamente');
    } catch (error) {
      console.error('❌ Error al cambiar estado:', error);

      // Error específico: columna faltante en la base de datos
      if (error.error?.includes("Unknown column 'fecha_pago'") || error.message?.includes("fecha_pago")) {
        await notify('error', 'Error de base de datos', 'La columna "fecha_pago" no existe en la tabla de cotizaciones.\n\nSolución: ALTER TABLE cotizaciones ADD COLUMN fecha_pago DATETIME NULL;\n\nO contacta al administrador del sistema.');
      }
      // Si el endpoint no existe, mostrar mensaje informativo
      else if (error.message?.includes('Ruta no encontrada') || error.message?.includes('404')) {
        await notify('warning', 'Función no disponible', 'El backend no tiene implementado el cambio de estado.\n\nContacta al administrador para actualizar el servidor.');
      } else {
        await notify('error', 'Error al actualizar estado', error.message || error.error || 'Intente nuevamente');
      }
    } finally {
      setUpdatingId(null);
    }
  };

  // Función para aplicar ISLR manualmente a una factura
  const handleCambiarISLR = async (facturaId, conceptoISLR) => {
    try {
      setUpdatingId(facturaId);

      // Actualizar localmente primero (optimistic update)
      setFacturas(prev => prev.map(f => {
        if (f.id !== facturaId) return f;

        return {
          ...f,
          islr_concepto_id: conceptoISLR.id,
          islr_concepto_codigo: conceptoISLR.codigo,
          islr_porcentaje: conceptoISLR.porcentaje,
          islr_aplica: conceptoISLR.porcentaje > 0
        };
      }));

      // "Sin ISLR" no tiene concepto que mandar -- PUT /islr exige
      // islr_concepto_id/codigo no nulos, por eso este caso usa el
      // DELETE dedicado en vez de reusar el mismo endpoint con nulls.
      if (conceptoISLR.id === null) {
        await cotizacionesService.quitarISLRFactura(facturaId);
      } else {
        await cotizacionesService.actualizarISLRFactura(facturaId, {
          islr_concepto_id: conceptoISLR.id,
          islr_concepto_codigo: conceptoISLR.codigo,
          islr_porcentaje: conceptoISLR.porcentaje
        });
      }

      setModalISLR(null);
      if (activeTab === 'recaudos') cargarRecaudos();
    } catch (error) {
      await notify('error', 'Atención', '❌ Error al actualizar ISLR: ' + (error.message || 'Intente nuevamente'));
    } finally {
      setUpdatingId(null);
    }
  };

  // Función para toggle lleva_factura
  const handleToggleLlevaFactura = async (facturaId, currentValue) => {
    const newValue = !(currentValue === true || currentValue === 1);
    try {
      setUpdatingLlevaFactura(facturaId);
      await cotizacionesService.update(facturaId, { lleva_factura: newValue });
      setFacturas(prev => prev.map(f =>
        f.id === facturaId ? { ...f, lleva_factura: newValue } : f
      ));
    } catch (error) {
      await notify('error', 'Atención', 'Error al actualizar estado de factura: ' + (error.message || 'Intente nuevamente'));
    } finally {
      setUpdatingLlevaFactura(null);
    }
  };

  // Helper: detectar si un estado es de tipo rechazado (soporta múltiples variantes)
  const isRechazado = (estado) => {
    if (!estado) return false;
    const estadoLower = estado.toLowerCase();
    return estadoLower === 'rechazada' || 
           estadoLower === 'rechazado' || 
           estadoLower === 'rejected' ||
           estadoLower === 'cancelada' ||
           estadoLower === 'cancelado' ||
           estadoLower.includes('rechaz') ||
           estadoLower.includes('cancel');
  };

  // Filtrar por pestaña activa y búsqueda
  const facturasFiltradas = facturas.filter(f => {
    const rechazado = isRechazado(f.estado);
    const pasadaAFactura = f.pasada_a_factura === true || f.pasada_a_factura === 1;

    // Filtro por pestaña. "recaudos" no usa este array -- tiene su propia
    // fuente de datos real via /api/recaudos (ver cargarRecaudos).
    if (activeTab === 'rechazadas') {
      if (!rechazado) return false;
    } else {
      // historial: mostrar solo pasadas a facturación
      if (!pasadaAFactura) return false;
    }

    // Filtro por búsqueda (solo para historial y rechazadas)
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return (
        (f.cotizacion_numero || '').toLowerCase().includes(term) ||
        (f.cliente_nombre || f.empresa_nombre || '').toLowerCase().includes(term)
      );
    }
    return true;
  });

  // Función para buscar proveedor o cliente y obtener su configuración de retención y tipo de persona
  const getProveedorInfo = (factura) => {
    // Buscar por nombre de cliente o empresa
    const nombreBuscar = factura.cliente_nombre || factura.empresa_nombre || '';
    if (!nombreBuscar) {
      return { esAgenteRetencion: false, porcentajeRetencion: 0, tipoPersona: 'natural' };
    }

    // Buscar primero en clientes
    if (clientesData && clientesData.length > 0) {
      const cliente = clientesData.find(c =>
        c.nombre?.toLowerCase().trim() === nombreBuscar.toLowerCase().trim() ||
        (c.cedula && c.cedula === factura.cliente_cedula) ||
        (c.rif && c.rif === factura.empresa_rif)
      );
      if (cliente) {
        return {
          esAgenteRetencion: cliente.agente_retencion || false,
          // Fraccion (dominio pct), no entero: 0.75 = 75%, no 75.
          porcentajeRetencion: cliente.porcentaje_retencion ?? 0.75,
          tipoPersona: cliente.tipo_persona || 'natural'
        };
      }
    }

    // Buscar proveedor que coincida (comparación flexible) - fallback
    if (proveedores && proveedores.length > 0) {
      const proveedor = proveedores.find(p =>
        p.nombre?.toLowerCase().trim() === nombreBuscar.toLowerCase().trim() ||
        (p.cedula && p.cedula === factura.cliente_cedula) ||
        (p.rif && p.rif === factura.empresa_rif)
      );

      if (proveedor) {
        return {
          esAgenteRetencion: proveedor.agente_retencion || false,
          porcentajeRetencion: proveedor.porcentaje_retencion ?? 0.75,
          tipoPersona: proveedor.tipo_persona || 'natural' // 'natural' (PN) o 'juridica' (PJ)
        };
      }
    }
    return { esAgenteRetencion: false, porcentajeRetencion: 0, tipoPersona: 'natural' };
  };

  // Contadores para las pestañas (se actualizan automáticamente al recibir datos)
  const countHistorial = facturas.filter(f => f.pasada_a_factura === true || f.pasada_a_factura === 1).length;
  const countRechazadas = facturas.filter(f => isRechazado(f.estado)).length;
  const countRecaudos = facturas.filter(f => (f.pasada_a_factura === true || f.pasada_a_factura === 1) && f.total > 0).length;
  const countComprasPendientes = comprasPendientes.filter(c => c.estado !== 'comprado' && c.estado !== 'nota_entrega_lista').length;
  const facturasRealesFiltradas = facturasReales.filter((factura) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return [factura.numero_factura, factura.cliente_nombre, factura.empresa_nombre, factura.cliente_rif]
      .some(valor => String(valor || '').toLowerCase().includes(term));
  });

  // Cargar compras pendientes desde el backend
  useEffect(() => {
    const cargarCompras = async () => {
      try {
        const response = await comprasPendientesService.getAll();
        if (response.success && response.data && Array.isArray(response.data)) {
          setComprasPendientes(response.data);
          console.log('📦 Compras cargadas desde backend:', response.data.length);
        }
      } catch (error) {
        console.error('❌ Error cargando compras pendientes:', error.message);
      }
    };
    
    cargarCompras();
  }, []);

  // Recargar compras cuando se activa la pestaña de Compras Pendientes
  useEffect(() => {
    if (activeTab === 'compras_pendientes') {
      const recargarDesdeBackend = async () => {
        try {
          const response = await comprasPendientesService.getAll();
          if (response.success && response.data && Array.isArray(response.data)) {
            setComprasPendientes(response.data);
            console.log('🔄 Compras recargadas desde backend:', response.data.length);
          }
        } catch (error) {
          console.error('❌ Error recargando compras:', error.message);
        }
      };
      
      recargarDesdeBackend();
    }
  }, [activeTab]);

  // Debug: Log cuando cambian las compras pendientes
  useEffect(() => {
    console.log('📊 FacturasView - comprasPendientes actualizadas:', comprasPendientes.length);
    console.log('📊 Datos:', comprasPendientes);
  }, [comprasPendientes]);

  // Las compras pendientes se guardan directamente vía API

  // Cargar validaciones de pago desde el backend al montar
  useEffect(() => {
    const cargarValidaciones = async () => {
      try {
        const response = await api.get('/validaciones-pago');
        if (response.data?.success && response.data?.data) {
          setValidacionesPago(response.data.data);
          console.log('💰 Validaciones de pago cargadas desde backend');
        }
      } catch (e) {
        console.info('No se pudieron cargar validaciones de pago:', e.message);
      }
    };
    cargarValidaciones();
  }, []);

  // Guardar validaciones de pago en el backend cuando cambien
  useEffect(() => {
    const guardarValidaciones = async () => {
      if (Object.keys(validacionesPago).length === 0) return;
      try {
        await api.put('/validaciones-pago', validacionesPago);
        const responseFacturas = await facturasService.getAll();
        const facturasData = responseFacturas.data?.data || responseFacturas.data || [];
        if (Array.isArray(facturasData)) setFacturasReales(facturasData);
      } catch (e) {
        console.warn('No se pudieron guardar validaciones de pago:', e.message);
      }
    };
    guardarValidaciones();
  }, [validacionesPago]);

  // Funciones CRUD para Compras Pendientes
  const guardarCompra = async () => {
    if (!formCompra.producto.trim()) {
      await notify('error', 'Atención', 'El nombre del producto es requerido');
      return;
    }

    // Validación para el nuevo flujo de pago
    if (formCompra.proveedor_vende === true) {
      if (!formCompra.monto_a_pagar || parseFloat(formCompra.monto_a_pagar) <= 0) {
        await notify('error', 'Atención', 'El monto a pagar es requerido cuando el proveedor vende');
        return;
      }
      if (formCompra.requiere_factura === null) {
        await notify('error', 'Atención', 'Por favor indique si requiere factura o no');
        return;
      }
      if (!formCompra.monto_transferencia || parseFloat(formCompra.monto_transferencia) <= 0) {
        await notify('error', 'Atención', 'El monto de transferencia es requerido');
        return;
      }
      if (!formCompra.referencia_bancaria.trim()) {
        await notify('error', 'Atención', 'La referencia bancaria es requerida');
        return;
      }
      if (!formCompra.fecha_pago) {
        await notify('error', 'Atención', 'La fecha de pago es requerida');
        return;
      }
      if (!formCompra.comprobante_pago) {
        await notify('error', 'Atención', 'El comprobante de pago es requerido');
        return;
      }
    }

    try {
      if (modalCompra === 'nueva') {
        const data = {
          ...formCompra,
          fecha_creacion: new Date().toISOString()
        };
        delete data.id;
        delete data.comprobante_pago;

        const response = await comprasPendientesService.create(data);
        if (response.success && response.data) {
          setComprasPendientes(prev => [response.data, ...prev]);
          console.log('✅ Compra creada vía API:', response.data.id);
        }
      } else {
        const data = { ...formCompra, fecha_actualizacion: new Date().toISOString() };
        delete data.comprobante_pago;

        const response = await comprasPendientesService.update(formCompra.id, data);
        if (response.success) {
          setComprasPendientes(prev => prev.map(c =>
            c.id === formCompra.id ? { ...c, ...data } : c
          ));
          console.log('✅ Compra actualizada vía API:', formCompra.id);
        }
      }
      cerrarModalCompra();
    } catch (error) {
      console.error('❌ Error guardando compra:', error);
      await notify('error', 'Atención', 'Error al guardar la compra. Intente de nuevo.');
    }
  };

  const eliminarCompra = async (id) => {
    if (!(await confirm('Eliminar compra', '¿Estás seguro de eliminar esta compra pendiente?'))) return;

    try {
      // Intentar eliminar del backend primero
      await comprasPendientesService.delete(id);
      console.log('✅ Compra eliminada del backend:', id);
    } catch (error) {
      console.error('❌ Error eliminando compra del backend:', error.message);
    }

    // Actualizar estado local
    setComprasPendientes(prev => prev.filter(c => c.id !== id));
  };

  const cambiarEstadoCompra = async (id, nuevoEstado) => {
    try {
      await comprasPendientesService.update(id, { estado: nuevoEstado });
      setComprasPendientes(prev => prev.map(c => 
        c.id === id ? { ...c, estado: nuevoEstado, fecha_actualizacion: new Date().toISOString() } : c
      ));
    } catch (error) {
      console.error('❌ Error actualizando estado:', error);
    }
  };

  const abrirModalNuevaCompra = (cotizacionOrigen = '', clienteNombre = '') => {
    setFormCompra({
      id: null,
      producto: '',
      cantidad: 1,
      descripcion: '',
      tipo: 'nacional',
      proveedor_sugerido: '',
      cotizacion_origen: cotizacionOrigen,
      cliente_nombre: clienteNombre,
      estado: 'pendiente',
      prioridad: 'media',
      notas: '',
      tracking_comentario: '',
      factura: '',
      receiver_name: '',
      receiver_id: '',
      deliverer_name: '',
      deliverer_id: '',
      fecha_creacion: null,
      // Nuevos campos para el flujo de pago
      proveedor_vende: null,
      requiere_factura: null,
      monto_a_pagar: '',
      porcentaje_retencion: '',
      monto_retencion_calculado: '',
      porcentaje_islr: '',
      monto_islr_calculado: '',
      monto_transferencia: '',
      referencia_bancaria: '',
      fecha_pago: '',
      comprobante_pago: null
    });
    setModalCompra('nueva');
  };

  const abrirModalEditarCompra = (compra) => {
    setFormCompra({ ...compra });
    setModalCompra('editar');
  };

  const cerrarModalCompra = () => {
    setModalCompra(null);
  };

  // Función para cargar proveedores
  const cargarProveedores = async () => {
    try {
      const response = await proveedoresService.getAll();
      if (response && Array.isArray(response.data)) {
        setListaProveedores(response.data);
      } else if (Array.isArray(response)) {
        setListaProveedores(response);
      }
    } catch (error) {
      console.error('Error al cargar proveedores:', error);
      // Fallback: proveedores de ejemplo si el backend falla
      setListaProveedores([
        { id: 1, nombre: 'Proveedor A', rif: 'J-12345678-9' },
        { id: 2, nombre: 'Proveedor B', rif: 'J-87654321-0' },
        { id: 3, nombre: 'Proveedor C', rif: 'J-11223344-5' }
      ]);
    }
  };

  // Función para abrir modal de proveedores
  const abrirModalProveedores = () => {
    cargarProveedores();
    setModalProveedores(true);
  };

  // Función para seleccionar proveedor
  const seleccionarProveedor = (proveedor) => {
    setFormCompra(prev => ({
      ...prev,
      proveedor_sugerido: proveedor.nombre
    }));
    setModalProveedores(false);
  };

  // Función para seleccionar proveedor externo
  const seleccionarProveedorExterno = () => {
    setFormCompra(prev => ({
      ...prev,
      proveedor_sugerido: 'PROVEEDOR EXTERNO'
    }));
    setModalProveedores(false);
  };

  // Función exportable para agregar compras desde cotizaciones (artículos sin stock)
  // Se llama automáticamente desde CotizacionesView cuando se detecta artículo sin stock
  const agregarCompraDesdeCotizacion = async (articuloData) => {
    const { 
      nombre, 
      cantidad, 
      descripcion, 
      via_compra, 
      link_proveedor,
      cotizacion_numero,
      cliente_nombre,
      empresa_nombre 
    } = articuloData;

    try {
      const response = await comprasPendientesService.create({
        producto: nombre,
        cantidad: cantidad || 1,
        descripcion: descripcion || `Sin stock - Vía: ${via_compra || 'nacional'}`,
        tipo: via_compra === 'internacional' ? 'internacional' : 'nacional',
        cotizacion_origen: cotizacion_numero || '',
        cliente_nombre: cliente_nombre || empresa_nombre || '',
        estado: 'pendiente',
        prioridad: 'alta',
        notas: link_proveedor ? `Link proveedor: ${link_proveedor}` : '',
        fecha_creacion: new Date().toISOString()
      });

      if (response.success && response.data) {
        setComprasPendientes(prev => [response.data, ...prev]);
        console.log(`✅ Artículo "${nombre}" agregado a Compras Pendientes vía API`);
      }
    } catch (error) {
      console.error('❌ Error guardando compra pendiente vía API:', error);
    }
  };

  // Funciones para Validación de Pago
  const abrirModalValidacionPago = (factura) => {
    const cotizacionId = factura.id;
    const validacionExistente = validacionesPago[cotizacionId];

    setFacturaResuelta(null); // se resuelve aparte, abajo (necesita un fetch)
    setFormValidacion({
      cliente_nombre: factura.cliente_nombre || factura.empresa_nombre || '',
      // Primera vez: se sugiere segun el flag real del cliente. Una vez
      // guardada la validacion, se respeta lo que quedo (por si alguien
      // lo corrigio a mano).
      cliente_agente_retencion: validacionExistente
        ? validacionExistente.esAgenteRetencionIVA
        : !!factura.cliente_agente_retencion,
      cuenta_bancaria_id: validacionExistente?.cuenta_bancaria_id || '',
      requiere_factura: validacionExistente?.requiere_factura ?? true,
      requiere_anticipo: validacionExistente?.requiere_anticipo || false,
      anticipo_monto_usd: validacionExistente?.anticipo_monto_usd || '',
      anticipo_tasa_cambio: validacionExistente?.anticipo_tasa_cambio || '',
      referencia: validacionExistente?.referencia || '',
      fecha: validacionExistente?.fecha || '',
      monto: validacionExistente?.monto || '',
      moneda: validacionExistente?.moneda || 'USD',
      tasa_cambio: validacionExistente?.tasa_cambio || '',
      captura: null,
      esAgenteRetencionIVA: validacionExistente
        ? validacionExistente.esAgenteRetencionIVA
        : !!factura.cliente_agente_retencion,
      numeroRetencionIVA: validacionExistente?.numeroRetencionIVA || '',
      fechaRetencionIVA: validacionExistente?.fechaRetencionIVA || '',
      montoRetencionIVA: validacionExistente?.montoRetencionIVA || '',
      capturaRetencionIVA: null,
      esServicioConISLR: validacionExistente?.esServicioConISLR || false,
      numeroRetencionISLR: validacionExistente?.numeroRetencionISLR || '',
      fechaRetencionISLR: validacionExistente?.fechaRetencionISLR || '',
      montoRetencionISLR: validacionExistente?.montoRetencionISLR || '',
      capturaRetencionISLR: null
    });
    setTabValidacionPago('pago');
    setModalValidacionPago({ cotizacionId, cotizacionNumero: factura.numero_cotizacion || factura.id });

    // Sin factura real emitida no hay retencion posible (la base de
    // IVA/ISLR sale de fn_emitir_factura, nunca se inventa) -- se busca
    // aparte para no bloquear la apertura del modal mientras responde.
    facturasService.getAll({ cotizacion_id: cotizacionId })
      .then((res) => {
        const facturas = res.data || [];
        const emitida = facturas.find(f => f.estado !== 'borrador' && f.estado !== 'anulada');
        setFacturaResuelta(emitida || null);
      })
      .catch((error) => {
        console.error('Error al buscar factura real de la cotización:', error);
        setFacturaResuelta(null);
      });
  };

  const cerrarModalValidacionPago = () => {
    setModalValidacionPago(null);
    setFacturaResuelta(null);
    setFormValidacion({
      cliente_nombre: '',
      cliente_agente_retencion: false,
      cuenta_bancaria_id: '',
      requiere_factura: false,
      requiere_anticipo: false,
      anticipo_monto_usd: '',
      anticipo_tasa_cambio: '',
      referencia: '',
      fecha: '',
      monto: '',
      moneda: 'USD',
      tasa_cambio: '',
      captura: null,
      esAgenteRetencionIVA: false,
      numeroRetencionIVA: '',
      fechaRetencionIVA: '',
      montoRetencionIVA: '',
      capturaRetencionIVA: null,
      esServicioConISLR: false,
      numeroRetencionISLR: '',
      fechaRetencionISLR: '',
      montoRetencionISLR: '',
      capturaRetencionISLR: null
    });
  };

  const handleUsarTasaBcvPago = async () => {
    setBuscandoTasa(true);
    try {
      const res = await configService.getTasaBcv();
      if (res.data?.valor) setFormValidacion(prev => ({ ...prev, tasa_cambio: res.data.valor }));
      else await notify('warning', 'Tasa no disponible', res.message || 'No se pudo obtener la tasa BCV, ingrésela manualmente');
    } catch (err) {
      await notify('warning', 'Tasa no disponible', err.message || 'No se pudo obtener la tasa BCV, ingrésela manualmente');
    } finally {
      setBuscandoTasa(false);
    }
  };

  const guardarValidacionPago = async () => {
    if (!modalValidacionPago) return;

    const { cotizacionId } = modalValidacionPago;

    if (validacionesPago[cotizacionId]?.conciliado) {
      await notify('warning', 'Atención', 'Esta validación ya fue conciliada contra un movimiento bancario y no se puede editar.');
      return;
    }

    // Validar campos requeridos
    if (!formValidacion.referencia.trim()) {
      await notify('error', 'Atención', 'La referencia bancaria es requerida');
      return;
    }
    if (!formValidacion.fecha) {
      await notify('error', 'Atención', 'La fecha es requerida');
      return;
    }
    if (!formValidacion.monto || parseFloat(formValidacion.monto) <= 0) {
      await notify('error', 'Atención', 'El monto debe ser mayor a 0');
      return;
    }
    if (formValidacion.moneda === 'VES' && (!formValidacion.tasa_cambio || parseFloat(formValidacion.tasa_cambio) <= 0)) {
      await notify('error', 'Atención', 'La tasa de cambio es requerida cuando el pago es en bolívares (para poder conciliarlo y compararlo con el monto en dólares)');
      return;
    }

    // Validar campos de retención IVA si es agente
    if (formValidacion.esAgenteRetencionIVA) {
      if (!facturaResuelta) {
        await notify('warning', 'Atención', 'Todavía no hay una factura emitida para esta cotización: la retención IVA se podrá registrar cuando exista.');
        return;
      }
      if (!formValidacion.numeroRetencionIVA.trim()) {
        await notify('error', 'Atención', 'El número de retención IVA es requerido');
        return;
      }
      if (!formValidacion.fechaRetencionIVA) {
        await notify('error', 'Atención', 'La fecha de retención IVA es requerida');
        return;
      }
      if (!formValidacion.montoRetencionIVA || parseFloat(formValidacion.montoRetencionIVA) <= 0) {
        await notify('error', 'Atención', 'El monto de retención IVA debe ser mayor a 0');
        return;
      }
    }

    // Validar campos de ISLR si es servicio con ISLR
    if (formValidacion.esServicioConISLR) {
      if (!facturaResuelta) {
        await notify('warning', 'Atención', 'Todavía no hay una factura emitida para esta cotización: la retención ISLR se podrá registrar cuando exista.');
        return;
      }
      if (!formValidacion.numeroRetencionISLR.trim()) {
        await notify('error', 'Atención', 'El número de retención ISLR es requerido');
        return;
      }
      if (!formValidacion.fechaRetencionISLR) {
        await notify('error', 'Atención', 'La fecha de retención ISLR es requerida');
        return;
      }
      if (!formValidacion.montoRetencionISLR || parseFloat(formValidacion.montoRetencionISLR) <= 0) {
        await notify('error', 'Atención', 'El monto de retención ISLR debe ser mayor a 0');
        return;
      }
    }

    if (formValidacion.requiere_anticipo) {
      if (!formValidacion.anticipo_monto_usd || parseFloat(formValidacion.anticipo_monto_usd) <= 0) {
        await notify('error', 'Atención', 'El monto del anticipo debe ser mayor a 0');
        return;
      }
      if (!formValidacion.anticipo_tasa_cambio || parseFloat(formValidacion.anticipo_tasa_cambio) <= 0) {
        await notify('error', 'Atención', 'La tasa de cambio del anticipo es requerida');
        return;
      }
    }

    const nuevaValidacion = {
      cotizacion_id: cotizacionId,
      factura_id: facturaResuelta?.id || null,
      quien_paga: formValidacion.cliente_nombre,
      requiere_factura: formValidacion.requiere_factura,
      requiere_anticipo: formValidacion.requiere_anticipo,
      anticipo_monto_usd: formValidacion.requiere_anticipo ? parseFloat(formValidacion.anticipo_monto_usd) : 0,
      anticipo_tasa_cambio: formValidacion.requiere_anticipo ? parseFloat(formValidacion.anticipo_tasa_cambio) : 0,
      referencia: formValidacion.referencia.trim(),
      fecha: formValidacion.fecha,
      monto: parseFloat(formValidacion.monto),
      moneda: formValidacion.moneda,
      tasa_cambio: formValidacion.moneda === 'VES' && formValidacion.tasa_cambio ? parseFloat(formValidacion.tasa_cambio) : null,
      cuenta_bancaria_id: formValidacion.cuenta_bancaria_id ? parseInt(formValidacion.cuenta_bancaria_id, 10) : null,
      capturaNombre: formValidacion.captura?.name || null,
      validadoAt: new Date().toISOString(),
      esAgenteRetencionIVA: formValidacion.esAgenteRetencionIVA,
      numeroRetencionIVA: formValidacion.esAgenteRetencionIVA ? formValidacion.numeroRetencionIVA.trim() : '',
      fechaRetencionIVA: formValidacion.esAgenteRetencionIVA ? formValidacion.fechaRetencionIVA : '',
      montoRetencionIVA: formValidacion.esAgenteRetencionIVA ? parseFloat(formValidacion.montoRetencionIVA) || 0 : 0,
      capturaRetencionIVANombre: formValidacion.esAgenteRetencionIVA ? formValidacion.capturaRetencionIVA?.name || null : null,
      esServicioConISLR: formValidacion.esServicioConISLR,
      numeroRetencionISLR: formValidacion.esServicioConISLR ? formValidacion.numeroRetencionISLR.trim() : '',
      fechaRetencionISLR: formValidacion.esServicioConISLR ? formValidacion.fechaRetencionISLR : '',
      montoRetencionISLR: formValidacion.esServicioConISLR ? parseFloat(formValidacion.montoRetencionISLR) || 0 : 0,
      capturaRetencionISLRNombre: formValidacion.esServicioConISLR ? formValidacion.capturaRetencionISLR?.name || null : null
    };

    setGuardandoValidacion(true);
    try {
      const respuesta = await validacionesPagoService.guardar({ [cotizacionId]: nuevaValidacion });
      const advertencias = respuesta.warnings?.[cotizacionId];
      if (advertencias?.length) {
        await notify('warning', 'Atención', advertencias.join('\n'));
      }
      setValidacionesPago(prev => ({
        ...prev,
        [cotizacionId]: { ...nuevaValidacion, conciliado: prev[cotizacionId]?.conciliado || false }
      }));
      console.log('✅ Validación de pago guardada para cotización:', cotizacionId);
      cerrarModalValidacionPago();
    } catch (error) {
      console.error('Error al guardar validación de pago:', error);
      await notify('error', 'Error', error.message || 'No se pudo guardar la validación de pago.');
    } finally {
      setGuardandoValidacion(false);
    }
  };

  const estaCotizacionValidada = (cotizacionId) => {
    const validacion = validacionesPago[cotizacionId];
    return validacion && validacion.referencia && validacion.fecha && validacion.monto;
  };

  const handleImprimirConValidacion = async (facturaId) => {
    if (!estaCotizacionValidada(facturaId)) {
      await notify('error', 'Atención', 'Esta cotización requiere validación de pago antes de imprimir.\n\nPor favor, haga clic en "Validar Pago" para ingresar los datos del pago (Referencia bancaria, Fecha y Monto).');
      return;
    }
    setImprimirId(facturaId);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar tipo de archivo (solo imágenes)
      if (!file.type.startsWith('image/')) {
        await notify('error', 'Atención', 'Por favor, seleccione solo archivos de imagen (JPG, PNG, etc.)');
        e.target.value = '';
        return;
      }
      // Validar tamaño máximo (5MB)
      if (file.size > 5 * 1024 * 1024) {
        await notify('error', 'Atención', 'El archivo es demasiado grande. Tamaño máximo: 5MB');
        e.target.value = '';
        return;
      }
      setFormValidacion(prev => ({ ...prev, captura: file }));
    }
  };

  const handleFileChangeRetencionIVA = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        await notify('error', 'Atención', 'Por favor, seleccione solo archivos de imagen (JPG, PNG, etc.)');
        e.target.value = '';
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        await notify('error', 'Atención', 'El archivo es demasiado grande. Tamaño máximo: 5MB');
        e.target.value = '';
        return;
      }
      setFormValidacion(prev => ({ ...prev, capturaRetencionIVA: file }));
    }
  };

  const handleFileChangeRetencionISLR = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        await notify('error', 'Atención', 'Por favor, seleccione solo archivos de imagen (JPG, PNG, etc.)');
        e.target.value = '';
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        await notify('error', 'Atención', 'El archivo es demasiado grande. Tamaño máximo: 5MB');
        e.target.value = '';
        return;
      }
      setFormValidacion(prev => ({ ...prev, capturaRetencionISLR: file }));
    }
  };

  // Filtrar compras pendientes
  const comprasFiltradas = comprasPendientes.filter(compra => {
    // Lógica de estados automáticos
    const getEstadoVirtual = () => {
      if (compra.estado === 'nota_entrega_lista') return 'nota_entrega_lista';
      if (compra.estado === 'comprado') return 'comprado';
      if (compra.tracking_comentario?.trim()) return 'lista_para_nota';
      return compra.estado;
    };
    
    const estadoVirtual = getEstadoVirtual();
    const matchEstado = filtroEstadoCompra === 'todos' || 
                        estadoVirtual === filtroEstadoCompra ||
                        compra.estado === filtroEstadoCompra;
    const matchTipo = filtroTipoCompra === 'todos' || compra.tipo === filtroTipoCompra;
    const matchSearch = searchCompra === '' || 
      compra.producto.toLowerCase().includes(searchCompra.toLowerCase()) ||
      compra.proveedor_sugerido?.toLowerCase().includes(searchCompra.toLowerCase()) ||
      compra.descripcion?.toLowerCase().includes(searchCompra.toLowerCase());
    return matchEstado && matchTipo && matchSearch;
  });

  // Función helper para paginación
  const getPaginatedData = (data, pagina) => {
    const inicio = (pagina - 1) * itemsPerPage;
    const fin = inicio + itemsPerPage;
    return {
      dataPaginada: data.slice(inicio, fin),
      totalPaginas: Math.ceil(data.length / itemsPerPage),
      inicio: inicio + 1,
      fin: Math.min(fin, data.length),
      total: data.length
    };
  };

  // Datos paginados
  const paginacionFacturas = getPaginatedData(facturasFiltradas, paginaFacturas);
  const paginacionCompras = getPaginatedData(comprasFiltradas, paginaCompras);
  const paginacionRecaudos = getPaginatedData(recaudosData, paginaRecaudos);

  // Resetear página cuando cambia itemsPerPage
  useEffect(() => {
    setPaginaFacturas(1);
    setPaginaCompras(1);
    setPaginaRecaudos(1);
  }, [itemsPerPage]);

  // Componente de Paginación
  const Pagination = ({ pagina, setPagina, totalPaginas, inicio, fin, total }) => {
    if (totalPaginas <= 1) return null;
    
    return (
      <div className="flex items-center justify-between px-6 py-4 bg-slate-50/80 border-t border-slate-200 backdrop-blur-sm">
  <div className="text-sm text-slate-500 font-normal tracking-wide">
    Mostrando <span className="font-semibold text-slate-900">{inicio}</span> – <span className="font-semibold text-slate-900">{fin}</span> de <span className="font-semibold text-slate-900">{total}</span>
  </div>
  
  <div className="flex items-center gap-2">
    <button
      onClick={() => setPagina(1)}
      disabled={pagina === 1}
      className="flex items-center justify-center w-9 h-9 text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-lg shadow-sm hover:bg-slate-50 hover:border-slate-400 hover:text-slate-900 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-slate-300 disabled:hover:bg-white transition-all duration-200 ease-out active:scale-95"
      aria-label="Primera página"
    >
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
      </svg>
    </button>
    
    <button
      onClick={() => setPagina(p => Math.max(1, p - 1))}
      disabled={pagina === 1}
      className="flex items-center justify-center w-9 h-9 text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-lg shadow-sm hover:bg-slate-50 hover:border-slate-400 hover:text-slate-900 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-slate-300 disabled:hover:bg-white transition-all duration-200 ease-out active:scale-95"
      aria-label="Página anterior"
    >
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
      </svg>
    </button>

    <div className="flex items-center px-4 py-1.5 mx-1 text-sm font-semibold text-slate-800 bg-white border border-slate-200 rounded-lg shadow-sm min-w-[5rem] justify-center select-none">
      <span className="tabular-nums text-slate-900">{pagina}</span>
      <span className="mx-1.5 text-slate-400 font-normal">/</span>
      <span className="tabular-nums text-slate-500">{totalPaginas}</span>
    </div>

    <button
      onClick={() => setPagina(p => Math.min(totalPaginas, p + 1))}
      disabled={pagina === totalPaginas}
      className="flex items-center justify-center w-9 h-9 text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-lg shadow-sm hover:bg-slate-50 hover:border-slate-400 hover:text-slate-900 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-slate-300 disabled:hover:bg-white transition-all duration-200 ease-out active:scale-95"
      aria-label="Página siguiente"
    >
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </button>
    
    <button
      onClick={() => setPagina(totalPaginas)}
      disabled={pagina === totalPaginas}
      className="flex items-center justify-center w-9 h-9 text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-lg shadow-sm hover:bg-slate-50 hover:border-slate-400 hover:text-slate-900 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-slate-300 disabled:hover:bg-white transition-all duration-200 ease-out active:scale-95"
      aria-label="Última página"
    >
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
      </svg>
    </button>
  </div>
</div>
    );
  };

const tabs = [
  {
    id: 'facturas_reales',
    label: 'Facturas reales',
    icon: FileText,
    count: facturasReales.length,
    badgeClass: 'bg-gradient-to-r from-indigo-600 to-indigo-500 text-white shadow-lg shadow-indigo-500/20 ring-1 ring-white/20',
    iconClass: 'text-indigo-600'
  },
  {
    id: 'historial',
    label: 'Cotizaciones Aceptadas',
    icon: History,
    count: countHistorial,
    badgeClass: 'bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-lg shadow-blue-500/20 ring-1 ring-white/20',
    iconClass: 'text-blue-600'
  },
  {
    id: 'rechazadas',
    label: 'Rechazadas',
    icon: XCircle,
    count: countRechazadas,
    badgeClass: 'bg-gradient-to-r from-red-500 to-red-400 text-white shadow-lg shadow-red-500/20 ring-1 ring-white/20',
    iconClass: 'text-red-500'
  },
  {
    id: 'recaudos',
    label: 'Recaudos IVA',
    icon: DollarSign,
    count: countRecaudos,
    badgeClass: 'bg-gradient-to-r from-emerald-600 to-emerald-500 text-white shadow-lg shadow-emerald-500/20 ring-1 ring-white/20',
    iconClass: 'text-emerald-600'
  },
  {
    id: 'compras_pendientes',
    label: 'Compras Pendientes',
    icon: ShoppingCart,
    count: countComprasPendientes,
    badgeClass: 'bg-gradient-to-r from-purple-600 to-purple-500 text-white shadow-lg shadow-purple-500/20 ring-1 ring-white/20',
    iconClass: 'text-purple-600'
  }
];

return (
  <div className="p-4 lg:p-6 bg-slate-50 min-h-screen">
    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
      <div>
        <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-3 tracking-tight">
          <FileSpreadsheet className="text-blue-600" size={28} />
          Facturación / Impresión
        </h2>
        <p className="text-slate-500 mt-1 text-sm">Visualiza e imprime tus documentos y cotizaciones</p>
      </div>
    </div>

    {/* Tabs estilo widgets */}
    <div className="bg-white rounded-2xl shadow-sm mb-6 overflow-hidden border border-slate-200/80">
      <div className="flex border-b border-slate-100">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`group flex items-center gap-2.5 px-6 py-4 font-medium text-sm transition-all duration-200 relative
                ${isActive
                  ? `${tab.iconClass} border-b-2 ${tab.iconClass.replace('text-', 'border-')} bg-white`
                  : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50/80 border-b-2 border-transparent'
                }`}
            >
              <Icon size={18} className={isActive ? tab.iconClass : 'text-slate-400 group-hover:text-slate-500 transition-colors'} />
              <span>{tab.label}</span>
              <span className={`ml-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold shadow-sm transition-all
                ${isActive
                  ? tab.badgeClass
                  : 'bg-slate-100 text-slate-500 group-hover:bg-slate-200/70'
                }`}>
                {tab.count}
              </span>
            </button>
          );
        })}
      </div>
      
      {/* Barra de búsqueda dentro del contenedor de tabs (no mostrar en recaudos) */}
      {activeTab !== 'recaudos' && (
        <div className="p-5 bg-slate-50/30">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={18} />
              <input
                type="text"
                placeholder={`Buscar en ${activeTab === 'historial' ? 'historial' : 'rechazadas'}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 placeholder:text-slate-400 focus:ring-2 focus:ring-blue-500/20 focus:outline-none focus:border-blue-500 transition-all shadow-sm"
              />
            </div>
            <select
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(Number(e.target.value))}
              className="px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-600 focus:ring-2 focus:ring-blue-500/20 focus:outline-none focus:border-blue-500 shadow-sm cursor-pointer hover:border-slate-300 transition-all"
            >
              <option value={5}>5 por página</option>
              <option value={10}>10 por página</option>
              <option value={25}>25 por página</option>
              <option value={50}>50 por página</option>
              <option value={100}>100 por página</option>
            </select>
          </div>
        </div>
      )}
    </div>

    <div className="bg-white rounded-2xl shadow-sm overflow-visible border border-slate-200/80">
      {loading ? (
        <div className="flex-1 flex py-20 items-center justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-[3px] border-slate-200 border-t-blue-600"></div>
        </div>
      ) : activeTab === 'facturas_reales' ? (
        <div className="p-6">
          {facturasRealesFiltradas.length === 0 ? (
            <div className="py-16 text-center text-slate-400">
              <FileText size={40} className="mx-auto mb-3 text-slate-300" />
              <p>No hay facturas reales para mostrar.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="bg-indigo-600 text-white">
                    <th className="px-4 py-3">Número</th>
                    <th className="px-4 py-3">Cliente / Empresa</th>
                    <th className="px-4 py-3">Fecha</th>
                    <th className="px-4 py-3">Estado</th>
                    <th className="px-4 py-3 text-right">Total</th>
                    <th className="px-4 py-3 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {facturasRealesFiltradas.map((factura) => (
                    <tr key={factura.id} className="hover:bg-indigo-50/40">
                      <td className="px-4 py-3 font-semibold text-indigo-700">{factura.numero_factura}</td>
                      <td className="px-4 py-3">
                        <span className="font-medium text-slate-700">{factura.cliente_nombre || factura.empresa_nombre || 'Sin tercero'}</span>
                        {factura.cliente_rif && <span className="block text-xs text-slate-400">{factura.cliente_rif}</span>}
                      </td>
                      <td className="px-4 py-3 text-slate-500">{factura.fecha_emision ? new Date(factura.fecha_emision).toLocaleDateString('es-VE') : 'Borrador'}</td>
                      <td className="px-4 py-3"><span className="px-2 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700">{factura.estado}</span></td>
                      <td className="px-4 py-3 text-right font-semibold text-slate-700">{factura.total != null ? fmtMonto(factura.total, factura.moneda) : '—'}</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => descargarFacturaPdf(factura)}
                          disabled={factura.estado === 'borrador'}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs hover:bg-indigo-700 disabled:bg-slate-300 disabled:cursor-not-allowed"
                        >
                          <Printer size={14} /> PDF
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : activeTab === 'recaudos' ? (
        // VISTA DE RECAUDOS IVA
        <div className="p-6">
          {/* Filtros de fecha y configuración de retenciones */}
          <div className="bg-slate-50/80 rounded-xl p-5 mb-6 space-y-4 border border-slate-200/80">
            <div className="flex items-center gap-2.5">
              <div className="p-1.5 bg-emerald-100 rounded-lg">
                <Filter size={18} className="text-emerald-700" />
              </div>
              <h3 className="font-semibold text-slate-800">Filtros y Configuración ISLR</h3>
              <span className="text-xs bg-amber-400 text-white px-3 py-1 rounded-full font-semibold shadow-sm shadow-amber-400/20 ml-2">
                La retención de IVA se configura individualmente por proveedor
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Fecha Inicio */}
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1.5">
                  <Calendar size={14} className="inline mr-1.5 text-slate-400" /> Fecha Inicio
                </label>
                <input
                  type="date"
                  value={recaudosFechaInicio}
                  onChange={(e) => setRecaudosFechaInicio(e.target.value)}
                  className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 focus:outline-none transition-all shadow-sm"
                />
              </div>

              {/* Fecha Fin */}
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1.5">
                  <Calendar size={14} className="inline mr-1.5 text-slate-400" /> Fecha Fin
                </label>
                <input
                  type="date"
                  value={recaudosFechaFin}
                  onChange={(e) => setRecaudosFechaFin(e.target.value)}
                  className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 focus:outline-none transition-all shadow-sm"
                />
              </div>

              {/* Items por página */}
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1.5">
                  Registros por página
                </label>
                <select
                  value={itemsPerPage}
                  onChange={(e) => setItemsPerPage(Number(e.target.value))}
                  className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 focus:outline-none transition-all shadow-sm cursor-pointer"
                >
                  <option value={5}>5 por página</option>
                  <option value={10}>10 por página</option>
                  <option value={25}>25 por página</option>
                  <option value={50}>50 por página</option>
                  <option value={100}>100 por página</option>
                </select>
              </div>

            </div>

            <div className="flex justify-end pt-1">
              <button
                onClick={() => {
                  setRecaudosFechaInicio('');
                  setRecaudosFechaFin('');
                }}
                className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 hover:text-slate-900 hover:border-slate-300 transition-all shadow-sm active:scale-95"
              >
                Limpiar filtros
              </button>
            </div>
          </div>

          {loadingRecaudos ? (
            <div className="py-20 flex items-center justify-center">
              <div className="w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : recaudosData.length === 0 ? (
            <div className="py-20 flex flex-col items-center justify-center text-slate-400">
              <div className="p-4 bg-slate-100 rounded-full mb-4">
                <DollarSign size={40} className="text-slate-300" />
              </div>
              <p className="text-base font-medium">No hay cotizaciones en el rango de fechas seleccionado</p>
            </div>
          ) : (
            <>
              {/* Tabla de IVA por cotización */}
              <div className="overflow-x-auto mb-6">
                <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2.5 bg-gradient-to-r from-emerald-600 to-emerald-500 p-3.5 rounded-xl shadow-lg shadow-emerald-500/20">
                  <FileText size={20} />
                  Detalle de IVA y Retenciones por Cotización
                </h3>
                <table className="w-full text-left border-collapse text-sm">
                  <thead>
                    <tr className="bg-gradient-to-r from-emerald-600 to-emerald-500">
                      <th className="px-4 py-3.5 font-semibold text-white text-xs uppercase tracking-wider">Cotización N°</th>
                      <th className="px-4 py-3.5 font-semibold text-white text-xs uppercase tracking-wider">Fecha</th>
                      <th className="px-4 py-3.5 font-semibold text-white text-xs uppercase tracking-wider">Cliente / Empresa</th>
                      <th className="px-4 py-3.5 font-semibold text-white/90 text-xs uppercase tracking-wider text-center">Agente<br/>Retención</th>
                      <th className="px-4 py-3.5 font-semibold text-white text-xs uppercase tracking-wider text-right">Base Imp.</th>
                      <th className="px-4 py-3.5 font-semibold text-white text-xs uppercase tracking-wider text-right">IVA</th>
                      <th className="px-4 py-3.5 font-semibold text-white/90 text-xs uppercase tracking-wider text-right">Retención<br/>IVA</th>
                      <th className="px-4 py-3.5 font-semibold text-white/90 text-xs uppercase tracking-wider text-right">ISLR<br/>(Manual)</th>
                      <th className="px-4 py-3.5 font-semibold text-white text-xs uppercase tracking-wider text-right">Depósito</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {paginacionRecaudos.dataPaginada.map((cot) => {
                      const infoRetencion = getProveedorInfo({ cliente_nombre: cot.cliente_nombre, empresa_nombre: cot.empresa_nombre });
                      const pctLabel = Math.round((infoRetencion.porcentajeRetencion || 0) * 100);
                      return (
                      <tr key={cot.id} className="hover:bg-slate-50/80 transition-colors">
                        <td className="px-4 py-3.5 font-semibold text-blue-600">
                          {cot.numero || `COT-${cot.id}`}
                        </td>
                        <td className="px-4 py-3.5 text-slate-500 text-xs font-medium">
                          {cot.fecha_creacion ? new Date(cot.fecha_creacion).toLocaleDateString('es-VE') : '-'}
                        </td>
                        <td className="px-4 py-3.5 text-slate-700 text-xs">
                          {cot.cliente_nombre || cot.empresa_nombre || '-'}
                          <span className="block text-[11px] text-slate-400 mt-0.5 font-medium">
                            {cot.tipo_persona === 'juridica' ? '(Persona Jurídica)' : '(Persona Natural)'} · {cot.moneda}
                          </span>
                        </td>
                        <td className="px-4 py-3.5 text-center">
                          {infoRetencion.esAgenteRetencion ? (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-400 text-white text-xs font-bold rounded-lg shadow-sm shadow-amber-400/25">
                              {pctLabel}%
                            </span>
                          ) : (
                            <span className="text-slate-300 text-xs">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3.5 text-right font-semibold text-slate-700 tabular-nums">
                          {fmtMonto(cot.base_imponible, cot.moneda)}
                          {cot.equivalente_ves && (
                            <span className="block text-[11px] font-normal text-slate-400">
                              ≈ {fmtMonto(cot.equivalente_ves.base_imponible, 'VES')}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3.5 text-right text-emerald-700 font-medium tabular-nums">
                          {fmtMonto(cot.iva, cot.moneda)}
                          {cot.equivalente_ves && (
                            <span className="block text-[11px] font-normal text-emerald-500/70">
                              ≈ {fmtMonto(cot.equivalente_ves.iva, 'VES')}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3.5 text-right text-amber-700 font-semibold tabular-nums">
                          {cot.retencion_iva > 0 ? fmtMonto(cot.retencion_iva, cot.moneda) : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="px-4 py-3.5 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => setModalISLR({
                                facturaId: cot.id,
                                tipoPersona: cot.tipo_persona || 'natural',
                                conceptoCodigo: cot.islr_detalle?.concepto,
                                porcentajeActual: cot.islr_detalle?.porcentaje || 0
                              })}
                              disabled={updatingId === cot.id}
                              className={`text-sm transition-all duration-200 ${
                                cot.islr_detalle
                                  ? 'text-red-600 hover:text-red-800 font-semibold hover:underline underline-offset-2'
                                  : 'text-slate-400 hover:text-slate-600 hover:underline underline-offset-2'
                              }`}
                              title="Click para seleccionar concepto ISLR"
                            >
                              {updatingId === cot.id ? (
                                <span className="text-slate-400">...</span>
                              ) : cot.islr_detalle ? (
                                <>
                                  {fmtMonto(cot.islr, cot.moneda)}
                                  <span className="block text-xs text-red-500 font-medium mt-0.5">
                                    {Math.round((cot.islr_detalle.porcentaje || 0) * 100)}%
                                  </span>
                                </>
                              ) : (
                                <span className="italic text-slate-400">Seleccionar...</span>
                              )}
                            </button>
                            {cot.islr_detalle ? (
                              <button
                                onClick={() => setModalISLRComprobante(cot)}
                                className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-all"
                                title="Vista Previa Comprobante ISLR"
                              >
                                <FileText size={15} />
                              </button>
                            ) : null}
                          </div>
                        </td>
                        <td className="px-4 py-3.5 text-right font-bold text-blue-700 bg-blue-50/60 tabular-nums">
                          {fmtMonto(cot.deposito, cot.moneda)}
                          {cot.equivalente_ves && (
                            <span className="block text-[11px] font-semibold text-blue-500">
                              ≈ {fmtMonto(cot.equivalente_ves.deposito, 'VES')}
                              <span className="block font-normal text-blue-400">
                                (tasa {cot.equivalente_ves.tasa} {cot.equivalente_ves.tasa_fuente}, de la factura)
                              </span>
                            </span>
                          )}
                          {infoRetencion.esAgenteRetencion && (
                            <span className="block text-xs font-medium text-blue-500/80 mt-1">
                              (con retención {pctLabel}%)
                            </span>
                          )}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Resumen de totales, agrupado por moneda: sumar USD con
                  VES en un solo numero no significa nada. */}
              {Object.entries(recaudosTotalesPorMoneda).map(([moneda, t]) => (
                <div key={moneda} className="bg-gradient-to-br from-emerald-600 to-emerald-500 rounded-xl p-6 mb-6 shadow-lg shadow-emerald-500/20">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <DollarSign className="text-white" size={20} />
                    Resumen de Totales y Retenciones — {moneda}
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
                      <p className="text-sm text-white/80 mb-1">Base Imponible</p>
                      <p className="text-xl font-bold text-white">{fmtMonto(t.base_imponible, moneda)}</p>
                    </div>
                    <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
                      <p className="text-sm text-white/80 mb-1">IVA Total</p>
                      <p className="text-xl font-bold text-white">{fmtMonto(t.iva, moneda)}</p>
                    </div>
                    <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
                      <p className="text-sm text-white/80 mb-1">Retención IVA</p>
                      <p className="text-xl font-bold text-white">{fmtMonto(t.retencion_iva, moneda)}</p>
                    </div>
                    <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
                      <p className="text-sm text-white/80 mb-1">ISLR</p>
                      <p className="text-xl font-bold text-white">{fmtMonto(t.islr, moneda)}</p>
                    </div>
                  </div>
                  <div className="bg-blue-600/30 rounded-xl p-4">
                    <p className="text-sm text-white mb-1 font-semibold">💰 Depósito a Pagar ({moneda})</p>
                    <p className="text-2xl font-bold text-white">{fmtMonto(t.deposito, moneda)}</p>
                    <p className="text-xs text-white/60 mt-1">Base + (IVA - Retención) - ISLR</p>
                  </div>
                </div>
              ))}

              {/* Total consolidado en Bs: lo nativo en VES + lo en USD ya
                  convertido con la tasa real de cada factura emitida. */}
              {recaudosTotalesVes && (
                <div className="bg-gradient-to-br from-indigo-700 to-indigo-600 rounded-xl p-6 mb-6 shadow-lg shadow-indigo-500/20">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <DollarSign className="text-white" size={20} />
                    Total en Bolívares (según tasa de cada factura)
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
                      <p className="text-sm text-white/80 mb-1">Base Imponible</p>
                      <p className="text-xl font-bold text-white">{fmtMonto(recaudosTotalesVes.base_imponible, 'VES')}</p>
                    </div>
                    <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
                      <p className="text-sm text-white/80 mb-1">IVA</p>
                      <p className="text-xl font-bold text-white">{fmtMonto(recaudosTotalesVes.iva, 'VES')}</p>
                    </div>
                    <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
                      <p className="text-sm text-white/80 mb-1">Retención IVA</p>
                      <p className="text-xl font-bold text-white">{fmtMonto(recaudosTotalesVes.retencion_iva, 'VES')}</p>
                    </div>
                    <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
                      <p className="text-sm text-white/80 mb-1">ISLR</p>
                      <p className="text-xl font-bold text-white">{fmtMonto(recaudosTotalesVes.islr, 'VES')}</p>
                    </div>
                  </div>
                  <div className="bg-indigo-900/30 rounded-xl p-4">
                    <p className="text-sm text-white mb-1 font-semibold">💰 Depósito Total a Pagar (Bs.)</p>
                    <p className="text-2xl font-bold text-white">{fmtMonto(recaudosTotalesVes.deposito, 'VES')}</p>
                  </div>
                </div>
              )}

              {/* SENIAT 1% / Alcaldia 3%: tasas fijas pedidas explicitamente
                  por el usuario, no vienen de una tabla de parametros (a
                  diferencia del IVA/ISLR de arriba, que si tienen respaldo
                  real en alicuotas_iva/islr_tarifas). */}
              {Object.entries(recaudosTotalesPorMoneda).map(([moneda, t]) => (
                <div key={`entidades-${moneda}`} className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div className="bg-gradient-to-br from-blue-600 to-blue-500 rounded-2xl p-6 shadow-lg shadow-blue-500/20">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-3 bg-white/20 rounded-full">
                        <Landmark className="text-white" size={24} />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-white">SENIAT ({moneda})</h3>
                        <p className="text-sm text-white/80">1% Base Imponible</p>
                      </div>
                    </div>
                    <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
                      <p className="text-2xl font-bold text-white">{fmtMonto(t.base_imponible * 0.01, moneda)}</p>
                      {moneda !== 'VES' && recaudosTotalesVes && (
                        <p className="text-xs text-white/60 mt-1">≈ {fmtMonto(recaudosTotalesVes.base_imponible * 0.01, 'VES')}</p>
                      )}
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-orange-500 to-amber-500 rounded-2xl p-6 shadow-lg shadow-orange-500/20">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-3 bg-white/20 rounded-full">
                        <Building className="text-white" size={24} />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-white">Alcaldía ({moneda})</h3>
                        <p className="text-sm text-white/80">3% Base Imponible</p>
                      </div>
                    </div>
                    <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
                      <p className="text-2xl font-bold text-white">{fmtMonto(t.base_imponible * 0.03, moneda)}</p>
                      {moneda !== 'VES' && recaudosTotalesVes && (
                        <p className="text-xs text-white/60 mt-1">≈ {fmtMonto(recaudosTotalesVes.base_imponible * 0.03, 'VES')}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {/* Paginación de recaudos */}
              {paginacionRecaudos.total > 0 && (
                <Pagination
                  pagina={paginaRecaudos}
                  setPagina={setPaginaRecaudos}
                  totalPaginas={paginacionRecaudos.totalPaginas}
                  inicio={paginacionRecaudos.inicio}
                  fin={paginacionRecaudos.fin}
                  total={paginacionRecaudos.total}
                />
              )}

              {/* Botón Vista Previa Comprobante Retención IVA */}
              <div className="flex justify-end mt-4">
                <button
                  onClick={() => setModalVistaPreviaRetIVA('preview')}
                  className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-emerald-600 to-emerald-500 text-white rounded-xl hover:from-emerald-700 hover:to-emerald-600 font-medium transition-all duration-200 shadow-lg shadow-emerald-500/20 active:scale-95"
                >
                  <FileText size={18} />
                  Vista Previa Comprobante Retención IVA
                </button>
              </div>
            </>
          )}
        </div>
      ) : activeTab === 'compras_pendientes' ? (
        // VISTA DE COMPRAS PENDIENTES
        <div className="p-6">
          {/* Header y acciones */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-gradient-to-br from-purple-600 to-purple-500 rounded-xl shadow-lg shadow-purple-500/20">
                <ShoppingCart className="text-white" size={24} />
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-800">Lista de Compras Pendientes</h3>
                <p className="text-sm text-slate-500">
                  {comprasFiltradas.length} productos pendientes por cotizar
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={async () => {
                  try {
                    const response = await comprasPendientesService.getAll();
                    if (response.success && response.data && Array.isArray(response.data)) {
                      setComprasPendientes(response.data);
                      console.log('🔄 Recarga manual desde backend:', response.data.length, 'compras');
                    }
                  } catch (error) {
                    console.error('Error recargando compras:', error.message);
                  }
                }}
                className="flex items-center gap-2 px-3 py-2 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 transition-all duration-200 font-medium text-sm active:scale-95"
                title="Recargar desde el servidor"
              >
                <RefreshCw size={18} />
                Recargar
              </button>
              <button
                onClick={() => abrirModalNuevaCompra()}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-all duration-200 font-medium shadow-sm active:scale-95"
              >
                <Plus size={20} />
                Agregar Producto
              </button>
            </div>
          </div>

          {/* Filtros */}
          <div className="bg-slate-50/80 rounded-xl p-5 mb-6 space-y-4 border border-slate-200/80">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Búsqueda */}
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                <input
                  type="text"
                  placeholder="Buscar producto, proveedor o descripción..."
                  value={searchCompra}
                  onChange={(e) => setSearchCompra(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 placeholder:text-slate-400 focus:ring-2 focus:ring-purple-500/20 focus:outline-none focus:border-purple-500 transition-all shadow-sm"
                />
              </div>
              {/* Filtro por estado */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-600 font-medium">Estado:</span>
                <select
                  value={filtroEstadoCompra}
                  onChange={(e) => setFiltroEstadoCompra(e.target.value)}
                  className="px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-600 focus:ring-2 focus:ring-purple-500/20 focus:outline-none focus:border-purple-500 shadow-sm cursor-pointer hover:border-slate-300 transition-all"
                >
                  <option value="todos">Todos</option>
                  <option value="pendiente">Pendiente</option>
                  <option value="en_cotizacion">En Cotización</option>
                  <option value="cotizado">Cotizado</option>
                  <option value="lista_para_nota">Lista para Nota</option>
                  <option value="comprado">Comprado</option>
                  <option value="nota_entrega_lista">Nota Entrega Lista</option>
                </select>
              </div>
              {/* Filtro por tipo */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-600 font-medium">Tipo:</span>
                <select
                  value={filtroTipoCompra}
                  onChange={(e) => setFiltroTipoCompra(e.target.value)}
                  className="px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-600 focus:ring-2 focus:ring-purple-500/20 focus:outline-none focus:border-purple-500 shadow-sm cursor-pointer hover:border-slate-300 transition-all"
                >
                  <option value="todos">Todos</option>
                  <option value="nacional">Nacional</option>
                  <option value="internacional">Internacional</option>
                </select>
              </div>
              {/* Items por página */}
              <select
                value={itemsPerPage}
                onChange={(e) => setItemsPerPage(Number(e.target.value))}
                className="px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-600 focus:ring-2 focus:ring-purple-500/20 focus:outline-none focus:border-purple-500 shadow-sm cursor-pointer hover:border-slate-300 transition-all"
              >
                <option value={5}>5 por página</option>
                <option value={10}>10 por página</option>
                <option value={25}>25 por página</option>
                <option value={50}>50 por página</option>
                <option value={100}>100 por página</option>
              </select>
            </div>
          </div>

          {/* Lista de compras */}
          {paginacionCompras.dataPaginada.length === 0 ? (
            <div className="py-20 flex flex-col items-center justify-center text-slate-400 bg-white rounded-2xl border border-dashed border-slate-300">
              <div className="p-4 bg-slate-100 rounded-full mb-4">
                <Package size={40} className="text-slate-300" />
              </div>
              <p className="text-lg font-medium mb-2">No hay compras pendientes</p>
              <p className="text-sm text-slate-400 mb-4">
                {searchCompra || filtroEstadoCompra !== 'todos' || filtroTipoCompra !== 'todos' 
                  ? 'Intenta ajustar los filtros de búsqueda' 
                  : 'Agrega productos que necesites cotizar desde cotizaciones existentes o manualmente'}
              </p>
              <button
                onClick={() => abrirModalNuevaCompra()}
                className="flex items-center gap-2 px-4 py-2.5 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-all duration-200 font-medium shadow-lg shadow-purple-500/20 active:scale-95"
              >
                <Plus size={18} />
                Agregar primera compra
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {paginacionCompras.dataPaginada.map((compra) => {
                // Estados automáticos basados en los datos de la compra
                const getEstadoAutomatico = () => {
                  if (compra.estado === 'nota_entrega_lista') return 'nota_entrega_lista';
                  if (compra.estado === 'comprado') return 'comprado';
                  if (compra.tracking_comentario && compra.tracking_comentario.trim()) return 'lista_para_nota';
                  if (compra.estado === 'cotizado') return 'cotizado';
                  if (compra.estado === 'en_cotizacion') return 'en_cotizacion';
                  return 'pendiente';
                };
                
                const estadoActual = getEstadoAutomatico();
                const esListaParaNota = estadoActual === 'lista_para_nota';
                
                // Función para obtener estilos de estado - clases completas estáticas
                const getEstadoStyles = (estado) => {
                  switch(estado) {
                    case 'pendiente':
                      return { bg: 'bg-slate-500', text: 'text-white', border: 'border-slate-500', icon: Clock, label: 'Pendiente' };
                    case 'en_cotizacion':
                      return { bg: 'bg-blue-500', text: 'text-white', border: 'border-blue-500', icon: Package, label: 'En Cotización' };
                    case 'cotizado':
                      return { bg: 'bg-amber-400', text: 'text-white', border: 'border-amber-400', icon: CheckCircle, label: 'Cotizado' };
                    case 'lista_para_nota':
                      return { bg: 'bg-cyan-500', text: 'text-white', border: 'border-cyan-500', icon: CheckCircle, label: 'Lista para Nota' };
                    case 'comprado':
                      return { bg: 'bg-emerald-500', text: 'text-white', border: 'border-emerald-500', icon: CheckCircle, label: 'Comprado' };
                    case 'nota_entrega_lista':
                      return { bg: 'bg-purple-500', text: 'text-white', border: 'border-purple-500', icon: Printer, label: 'Nota Entrega Lista' };
                    default:
                      return { bg: 'bg-slate-500', text: 'text-white', border: 'border-slate-500', icon: Clock, label: 'Pendiente' };
                  }
                };
                const estadoStyle = getEstadoStyles(compra.estado);
                const EstadoIcon = estadoStyle.icon;

                // Función para obtener estilos de prioridad - clases completas estáticas
                const getPrioridadStyles = (prioridad) => {
                  switch(prioridad) {
                    case 'baja':
                      return 'bg-slate-500 text-white';
                    case 'media':
                      return 'bg-amber-500 text-white';
                    case 'alta':
                      return 'bg-red-500 text-white';
                    default:
                      return 'bg-slate-500 text-white';
                  }
                };
                
                return (
                  <div
                    key={compra.id}
                    className={`bg-white rounded-xl p-5 border-2 transition-all duration-200 hover:shadow-md hover:border-slate-300 ${estadoStyle.border}`}
                  >
                    <div className="flex flex-col md:flex-row gap-4">
                      {/* Info principal */}
                      <div className="flex-1">
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-lg ${estadoStyle.bg}`}>
                            <EstadoIcon className={estadoStyle.text} size={20} />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-semibold text-slate-800 text-lg">{compra.producto}</h4>
                            {/* Mostrar nombre del cliente/empresa si existe */}
                            {compra.cliente_nombre && (
                              <p className="text-sm font-medium text-purple-600 mt-1 flex items-center gap-1">
                                <span>👤</span> Cliente: {compra.cliente_nombre}
                              </p>
                            )}
                            {compra.descripcion && (
                              <p className="text-sm text-slate-500 mt-1">{compra.descripcion}</p>
                            )}
                            <div className="flex flex-wrap items-center gap-2 mt-2">
                              {/* Badge de estado automático */}
                              <span className={`px-2.5 py-1 rounded-lg text-xs font-semibold shadow-sm ${estadoStyle.bg} ${estadoStyle.text}`}>
                                {esListaParaNota ? '✓ Lista para Nota' : estadoStyle.label}
                              </span>
                              <span className={`px-2.5 py-1 rounded-lg text-xs font-semibold shadow-sm ${getPrioridadStyles(compra.prioridad)}`}>
                                Prioridad: {compra.prioridad}
                              </span>
                              <span className={`px-2.5 py-1 rounded-lg text-xs font-semibold shadow-sm ${compra.tipo === 'internacional' ? 'bg-cyan-500 text-white' : 'bg-emerald-500 text-white'}`}>
                                {compra.tipo === 'internacional' ? '🌍 Internacional' : '🏠 Nacional'}
                              </span>
                              {compra.cotizacion_origen && (
                                <span className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-slate-500 text-white shadow-sm">
                                  Origen: {compra.cotizacion_origen}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Detalles */}
                      <div className="flex flex-wrap md:flex-col items-start md:items-end gap-2 md:gap-1 text-sm">
                        <div className="flex items-center gap-2 text-slate-600">
                          <span className="font-medium">Cantidad:</span>
                          <span>{compra.cantidad}</span>
                        </div>
                        {compra.proveedor_sugerido && (
                          <div className="flex items-center gap-2 text-slate-600">
                            <span className="font-medium">Proveedor:</span>
                            <span>{compra.proveedor_sugerido}</span>
                          </div>
                        )}
                        <div className="text-xs text-slate-400">
                          Creado: {new Date(compra.fecha_creacion).toLocaleDateString('es-VE')}
                        </div>
                      </div>

                      {/* Acciones */}
                      <div className="flex md:flex-col items-center md:items-end gap-2">
                        {/* Botón Nota de Entrega - aparece en azul cuando tiene tracking */}
                        {esListaParaNota && compra.estado !== 'nota_entrega_lista' && (
                          <button
                            onClick={() => setImprimirNotaCompra(compra)}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all duration-200 text-xs font-medium mb-1 shadow-sm active:scale-95"
                          >
                            <Printer size={14} />
                            Nota de Entrega
                          </button>
                        )}
                        
                        {/* Si ya está en estado nota_entrega_lista, mostrar badge */}
                        {compra.estado === 'nota_entrega_lista' && (
                          <span className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-500 text-white rounded-xl text-xs font-semibold mb-1 shadow-sm shadow-blue-500/20">
                            <Printer size={14} />
                            Nota Entregada
                          </span>
                        )}
                        
                        <div className="flex gap-1">
                          {/* Botón imprimir solo visible cuando hay tracking o ya tiene nota */}
                          {(compra.tracking_comentario?.trim() || compra.estado === 'nota_entrega_lista') && (
                            <button
                              onClick={() => setImprimirNotaCompra(compra)}
                              className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all duration-200 active:scale-95"
                              title="Ver/Imprimir Nota de Entrega"
                            >
                              <Printer size={18} />
                            </button>
                          )}
                          <button
                            onClick={() => abrirModalEditarCompra(compra)}
                            className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all duration-200 active:scale-95"
                            title="Editar"
                          >
                            <Edit2 size={18} />
                          </button>
                          <button
                            onClick={() => eliminarCompra(compra.id)}
                            className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all duration-200 active:scale-95"
                            title="Eliminar"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                        
                        {/* Indicador de estado automático (no editable) */}
                        <div className="text-xs text-slate-500 italic">
                          Estado: {estadoStyle.label}
                        </div>
                      </div>
                    </div>
                    {compra.notas && (
                      <div className="mt-3 pt-3 border-t border-slate-100">
                        <p className="text-sm text-slate-500 italic">{compra.notas}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {paginacionCompras.total > 0 && (
            <Pagination
              pagina={paginaCompras}
              setPagina={setPaginaCompras}
              totalPaginas={paginacionCompras.totalPaginas}
              inicio={paginacionCompras.inicio}
              fin={paginacionCompras.fin}
              total={paginacionCompras.total}
            />
          )}

          {/* Resumen */}
          {comprasFiltradas.length > 0 && (
            <div className="mt-6 bg-purple-50/80 rounded-xl p-5 border border-purple-200/80">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-purple-700">{comprasPendientes.filter(c => c.estado === 'pendiente').length}</p>
                    <p className="text-xs text-purple-600">Pendientes</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-blue-700">{comprasPendientes.filter(c => c.estado === 'en_cotizacion').length}</p>
                    <p className="text-xs text-blue-600">En Cotización</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-amber-600">{comprasPendientes.filter(c => c.estado === 'cotizado').length}</p>
                    <p className="text-xs text-amber-600">Cotizados</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-cyan-600">
                      {comprasPendientes.filter(c => c.tracking_comentario?.trim() && c.estado !== 'nota_entrega_lista' && c.estado !== 'comprado').length}
                    </p>
                    <p className="text-xs text-cyan-600">Listas para Nota</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-emerald-700">{comprasPendientes.filter(c => c.estado === 'comprado').length}</p>
                    <p className="text-xs text-emerald-600">Comprados</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-purple-700">{comprasPendientes.filter(c => c.estado === 'nota_entrega_lista').length}</p>
                    <p className="text-xs text-purple-600">Notas Listas</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-purple-600">
                    Total de productos: <span className="font-bold text-purple-800">{comprasPendientes.length}</span>
                  </p>
                  <p className="text-xs text-purple-500">
                    {comprasPendientes.filter(c => c.tipo === 'internacional').length} internacionales, {comprasPendientes.filter(c => c.tipo === 'nacional').length} nacionales
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : facturasFiltradas.length === 0 ? (
        <div className="py-20 flex flex-col items-center justify-center text-slate-400">
          {activeTab === 'rechazadas' ? (
            <>
              <div className="p-4 bg-slate-100 rounded-full mb-4">
                <XCircle size={40} className="text-slate-300" />
              </div>
              <p className="text-lg font-medium">No hay cotizaciones rechazadas</p>
            </>
          ) : (
            <>
              <div className="p-4 bg-slate-100 rounded-full mb-4">
                <FileText size={40} className="text-slate-300" />
              </div>
              <p className="text-lg font-medium">No se encontraron documentos</p>
            </>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto max-h-[calc(100vh-300px)] rounded-2xl bg-white shadow-xl border border-slate-200/80">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 border-b-2 border-slate-200">
                <th className="px-4 py-3.5 font-bold text-slate-900 text-xs uppercase tracking-wider">Doc N°</th>
                <th className="px-4 py-3.5 font-bold text-slate-900 text-xs uppercase tracking-wider">Fecha</th>
                <th className="px-4 py-3.5 font-bold text-slate-900 text-xs uppercase tracking-wider">Cliente / Empresa</th>
                <th className="px-4 py-3.5 font-bold text-slate-900 text-xs uppercase tracking-wider">Estado</th>
                <th className="px-4 py-3.5 font-bold text-slate-900 text-xs uppercase tracking-wider text-center">Vía de Compra</th>
                <th className="px-4 py-3.5 font-bold text-slate-900 text-xs uppercase tracking-wider text-right">Total</th>
                <th className="px-4 py-3.5 font-bold text-slate-900 text-xs uppercase tracking-wider text-center">Acción</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {paginacionFacturas.dataPaginada.map((factura) => {
                // Determinar la vía de compra predominante
                const vias = factura.resumen_vias_compra || {};
                const tieneNacional = (vias.nacional || 0) > 0;
                const tieneInternacional = (vias.internacional || 0) > 0;
                const viaPrincipal = factura.via_compra || (tieneNacional && tieneInternacional ? 'mixta' : tieneNacional ? 'nacional' : tieneInternacional ? 'internacional' : '-');

                return (
                  <tr key={factura.id} className="bg-white hover:bg-slate-50/80 transition-colors border-b border-slate-100">
                    <td className="px-4 py-4 font-bold text-blue-600 text-sm">
                        {factura.cotizacion_numero || `COT-${factura.id}`}
                    </td>
                    <td className="px-4 py-4 text-slate-700 text-sm font-medium">
                      {factura.fecha_creacion ? new Date(factura.fecha_creacion).toLocaleDateString('es-VE') : '-'}
                    </td>
                    <td className="px-4 py-4 text-slate-900 font-semibold text-sm max-w-[180px]">
                      <div className="flex flex-col gap-1">
                        <span className="break-words">{factura.cliente_nombre || factura.empresa_nombre || '-'}</span>
                        {(() => {
                          const infoRetencion = getProveedorInfo(factura);
                          if (infoRetencion.esAgenteRetencion) {
                            const pctLabel = Math.round((infoRetencion.porcentajeRetencion || 0) * 100);
                            return (
                              <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold bg-orange-50 text-orange-700 w-fit border border-orange-200" title={`Agente de Retención (${pctLabel}%)`}>
                                AR {pctLabel}%
                              </span>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2 flex-wrap">
                      {(() => {
                        const estadoMostrar = getEstadoFactura(factura);

                        // Función para obtener el badge según el estado - clases completas estáticas
                        const getEstadoBadge = (estado) => {
                          const baseClasses = "inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide border cursor-pointer hover:brightness-110 transition-all duration-200 shadow-sm active:scale-95";

                          switch(estado) {
                            case 'confirmada':
                              return (
                                <button
                                  onClick={() => setModalEstado({ facturaId: factura.id, estadoActual: factura.estado })}
                                  disabled={updatingId === factura.id}
                                  className={`${baseClasses} bg-emerald-500 text-white border-emerald-400`}
                                >
                                  {updatingId === factura.id ? (
                                    <>
                                      <span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
                                      Actualizando...
                                    </>
                                  ) : (
                                    <>
                                      {estadoMostrar.replace(/_/g, ' ')}
                                      <svg className="w-3 h-3 opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                      </svg>
                                    </>
                                  )}
                                </button>
                              );
                            case 'rechazada':
                              return (
                                <button
                                  onClick={() => setModalEstado({ facturaId: factura.id, estadoActual: factura.estado })}
                                  disabled={updatingId === factura.id}
                                  className={`${baseClasses} bg-red-500 text-white border-red-400`}
                                >
                                  {updatingId === factura.id ? (
                                    <>
                                      <span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
                                      Actualizando...
                                    </>
                                  ) : (
                                    <>
                                      {estadoMostrar.replace(/_/g, ' ')}
                                      <svg className="w-3 h-3 opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                      </svg>
                                    </>
                                  )}
                                </button>
                              );
                            case 'en_espera_pago':
                              return (
                                <button
                                  onClick={() => setModalEstado({ facturaId: factura.id, estadoActual: factura.estado })}
                                  disabled={updatingId === factura.id}
                                  className={`${baseClasses} bg-blue-500 text-white border-blue-400`}
                                >
                                  {updatingId === factura.id ? (
                                    <>
                                      <span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
                                      Actualizando...
                                    </>
                                  ) : (
                                    <>
                                      {estadoMostrar.replace(/_/g, ' ')}
                                      <svg className="w-3 h-3 opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                      </svg>
                                    </>
                                  )}
                                </button>
                              );
                            case 'lista_confirmar':
                              return (
                                <button
                                  onClick={() => setModalEstado({ facturaId: factura.id, estadoActual: factura.estado })}
                                  disabled={updatingId === factura.id}
                                  className={`${baseClasses} bg-amber-400 text-white border-amber-300`}
                                >
                                  {updatingId === factura.id ? (
                                    <>
                                      <span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
                                      Actualizando...
                                    </>
                                  ) : (
                                    <>
                                      {estadoMostrar.replace(/_/g, ' ')}
                                      <svg className="w-3 h-3 opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                      </svg>
                                    </>
                                  )}
                                </button>
                              );
                            case 'borrador':
                              return (
                                <button
                                  onClick={() => setModalEstado({ facturaId: factura.id, estadoActual: factura.estado })}
                                  disabled={updatingId === factura.id}
                                  className={`${baseClasses} bg-slate-500 text-white border-slate-400`}
                                >
                                  {updatingId === factura.id ? (
                                    <>
                                      <span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
                                      Actualizando...
                                    </>
                                  ) : (
                                    <>
                                      {estadoMostrar.replace(/_/g, ' ')}
                                      <svg className="w-3 h-3 opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                      </svg>
                                    </>
                                  )}
                                </button>
                              );
                            case 'en_proceso':
                              return (
                                <button
                                  onClick={() => setModalEstado({ facturaId: factura.id, estadoActual: factura.estado })}
                                  disabled={updatingId === factura.id}
                                  className={`${baseClasses} bg-orange-500 text-white border-orange-400`}
                                >
                                  {updatingId === factura.id ? (
                                    <>
                                      <span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
                                      Actualizando...
                                    </>
                                  ) : (
                                    <>
                                      {estadoMostrar.replace(/_/g, ' ')}
                                      <svg className="w-3 h-3 opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                      </svg>
                                    </>
                                  )}
                                </button>
                              );
                            case 'pagada':
                              return (
                                <button
                                  onClick={() => setModalEstado({ facturaId: factura.id, estadoActual: factura.estado })}
                                  disabled={updatingId === factura.id}
                                  className={`${baseClasses} bg-purple-500 text-white border-purple-400`}
                                >
                                  {updatingId === factura.id ? (
                                    <>
                                      <span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
                                      Actualizando...
                                    </>
                                  ) : (
                                    <>
                                      {estadoMostrar.replace(/_/g, ' ')}
                                      <svg className="w-3 h-3 opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                      </svg>
                                    </>
                                  )}
                                </button>
                              );
                            default:
                              return (
                                <button
                                  onClick={() => setModalEstado({ facturaId: factura.id, estadoActual: factura.estado })}
                                  disabled={updatingId === factura.id}
                                  className={`${baseClasses} bg-amber-400 text-white border-amber-300`}
                                >
                                  {updatingId === factura.id ? (
                                    <>
                                      <span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
                                      Actualizando...
                                    </>
                                  ) : (
                                    <>
                                      {estadoMostrar.replace(/_/g, ' ')}
                                      <svg className="w-3 h-3 opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                      </svg>
                                    </>
                                  )}
                                </button>
                              );
                          }
                        };

                        return getEstadoBadge(estadoMostrar);
                      })()}
                      {(() => {
                        const estadoFacturacion = getEstadoFacturacion(factura);
                        return (
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold border ${estadoFacturacion.clase}`}>
                            {estadoFacturacion.etiqueta}
                          </span>
                        );
                      })()}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center whitespace-nowrap">
                      {viaPrincipal === 'nacional' && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wide bg-emerald-500 text-white border border-emerald-400 shadow-sm">
                          🏠 Nacional
                        </span>
                      )}
                      {viaPrincipal === 'internacional' && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wide bg-cyan-500 text-white border border-cyan-400 shadow-sm">
                          <Globe size={12} /> Internacional
                        </span>
                      )}
                      {viaPrincipal === 'mixta' && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wide bg-violet-500 text-white border border-violet-400 shadow-sm">
                          🏠+🌍 Mixta
                        </span>
                      )}
                      {viaPrincipal === '-' && (
                        <span className="text-slate-300 text-xs">-</span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-right whitespace-nowrap">
                      {(() => {
                        const resumenCobro = getResumenCobro(factura);
                        return (
                          <div className="flex flex-col items-end gap-1">
                            {resumenCobro ? (
                              <>
                                <span className="font-bold text-emerald-700 text-sm tabular-nums">
                                  Esperado: {fmtMonto(resumenCobro.total, resumenCobro.moneda)}
                                </span>
                                {resumenCobro.equivalenteVes !== null && (
                                  <span className="text-xs text-slate-500 tabular-nums">
                                    En cuenta: {fmtMonto(resumenCobro.equivalenteVes, 'VES')}
                                  </span>
                                )}
                                <span className="text-[11px] text-slate-400">
                                  Tasa: {resumenCobro.tasa > 0 ? resumenCobro.tasa : '—'} {resumenCobro.tasa > 0 ? `(${resumenCobro.factura.tasa_fuente || 'sin fuente'})` : ''}
                                </span>
                                {resumenCobro.factura.estado === 'pagada' && (
                                  <span className="text-xs font-semibold text-emerald-700">Cobrado: {fmtMonto(resumenCobro.total, resumenCobro.moneda)}</span>
                                )}
                              </>
                            ) : (
                              <span className="font-bold text-slate-500 text-sm tabular-nums">
                                {fmtMonto(factura.total || 0, factura.moneda || 'USD')}
                              </span>
                            )}
                        {/* Badge de Anticipo con botón para ver tabla mixta */}
                        {(factura.requiere_anticipo || validacionesPago[factura.id]?.requiere_anticipo) && (
                          <button
                            onClick={() => setModalAnticipo({ 
                              factura,
                              anticipoData: {
                                requiere_anticipo: true,
                                anticipo_monto_usd: validacionesPago[factura.id]?.anticipo_monto_usd || factura.anticipo_monto_usd,
                                anticipo_tasa_cambio: validacionesPago[factura.id]?.anticipo_tasa_cambio || factura.anticipo_tasa_cambio
                              }
                            })}
                            className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-[10px] font-semibold border border-purple-300 hover:bg-purple-200 transition-colors"
                          >
                            <DollarSign size={8} />
                            Ver Anticipo
                          </button>
                        )}
                        {validacionesPago[factura.id]?.requiere_factura && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px] font-semibold border border-blue-300">
                            <FileText size={8} />
                            Con Factura
                          </span>
                        )}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-3 py-3 text-center">
                      <div className="flex flex-col gap-1.5">
                        {factura.estado === 'rechazada' ? (
                          // Si está rechazada, no mostrar controles de validación
                          <span className="text-[10px] text-slate-400">-</span>
                        ) : estaCotizacionValidada(factura.id) ? (
                          // Si está validada, mostrar botón de imprimir (verde) y editar validación
                          <>
                            <button
                              onClick={() => handleImprimirConValidacion(factura.id)}
                              className="inline-flex items-center justify-center gap-1 px-3 py-1.5 bg-emerald-600 border border-emerald-500 rounded-xl font-bold text-[11px] text-white uppercase tracking-wide hover:brightness-110 transition-all duration-200 shadow-sm active:scale-95"
                            >
                              <Printer size={12} /> Imprimir
                            </button>
                            <button
                              onClick={() => abrirModalValidacionPago(factura)}
                              className="inline-flex items-center justify-center gap-1 px-3 py-1 bg-blue-500 border border-blue-400 rounded-xl font-bold text-[11px] text-white hover:brightness-110 transition-all duration-200 shadow-sm active:scale-95"
                              title="Editar validación de pago"
                            >
                              <CheckCircle size={10} /> Validado
                            </button>
                          </>
                        ) : (
                          // Si no está validada, mostrar botón de validar
                          <>
                            <button
                              onClick={() => abrirModalValidacionPago(factura)}
                              className="inline-flex items-center justify-center gap-1 px-3 py-1.5 bg-orange-500 border border-orange-400 rounded-xl font-bold text-[11px] text-white uppercase tracking-wide hover:brightness-110 transition-all duration-200 shadow-sm active:scale-95"
                            >
                              <DollarSign size={12} /> Validar
                            </button>
                            <span className="text-[10px] text-slate-400 leading-tight">Requiere validación</span>
                          </>
                        )}
                        <button
                          onClick={() => abrirDetalleCotizacion(factura)}
                          disabled={cargandoDetalle}
                          className="inline-flex items-center justify-center gap-1 px-3 py-1.5 bg-slate-600 border border-slate-500 rounded-xl font-bold text-[11px] text-white uppercase tracking-wide hover:bg-slate-700 transition-colors disabled:opacity-50"
                        >
                          <Eye size={12} /> Ver detalle
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {/* Paginación Facturas */}
          <Pagination
            pagina={paginaFacturas}
            setPagina={setPaginaFacturas}
            totalPaginas={paginacionFacturas.totalPaginas}
            inicio={paginacionFacturas.inicio}
            fin={paginacionFacturas.fin}
            total={paginacionFacturas.total}
          />
        </div>
      )}
    </div>

    {imprimirId && (
      <FacturaPrintModal 
        cotizacionId={imprimirId} 
        onClose={() => setImprimirId(null)} 
      />
    )}

    {/* Modal para cambiar estado */}
    {modalEstado && (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full border border-slate-200/80">
          <div className="p-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">Cambiar Estado</h3>
            <div className="space-y-2">
              {[
                { value: 'borrador', label: 'Borrador', color: 'bg-slate-500' },
                { value: 'en_proceso', label: 'En Proceso', color: 'bg-orange-500' },
                { value: 'lista_confirmar', label: 'Lista Confirmar', color: 'bg-amber-400' },
                { value: 'confirmada', label: 'Confirmada', color: 'bg-emerald-500' },
                { value: 'en_espera_pago', label: 'En Espera de Pago', color: 'bg-blue-500' },
                { value: 'pagada', label: 'Pagada', color: 'bg-purple-500' },
                { value: 'rechazada', label: 'Rechazada', color: 'bg-red-500' }
              ].map((estado) => (
                <button
                  key={estado.value}
                  onClick={() => handleCambiarEstado(modalEstado.facturaId, estado.value)}
                  disabled={updatingId === modalEstado.facturaId || modalEstado.estadoActual === estado.value}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all duration-200 ${
                    modalEstado.estadoActual === estado.value
                      ? 'bg-slate-100 font-semibold cursor-default'
                      : 'hover:bg-slate-50'
                  }`}
                >
                  <span className={`w-3 h-3 rounded-full ${estado.color}`}></span>
                  <span className="flex-1 text-sm">{estado.label}</span>
                  {modalEstado.estadoActual === estado.value && (
                    <span className="text-xs text-emerald-600 font-medium">Actual</span>
                  )}
                </button>
              ))}
            </div>
            <button
              onClick={() => setModalEstado(null)}
              className="mt-6 w-full py-2.5 px-4 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-sm font-medium transition-all duration-200 active:scale-95"
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Modal para Validación de Pago */}
    {modalValidacionPago && (() => {
      const bloqueadoPorConciliacion = !!validacionesPago[modalValidacionPago.cotizacionId]?.conciliado;
      return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-slate-200/80">
          <div className="p-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-1">
              Validación de Pago
            </h3>
            <p className="text-sm text-slate-500 mb-4">
              Cotización: <span className="font-medium text-slate-700">{modalValidacionPago.cotizacionNumero}</span>
            </p>

            {bloqueadoPorConciliacion && (
              <div className="mb-4 p-3 bg-slate-100 border border-slate-300 rounded-xl text-sm text-slate-600 flex items-center gap-2">
                <Info size={16} className="text-slate-400 flex-shrink-0" />
                Esta validación ya fue conciliada contra un movimiento bancario: queda de solo lectura.
              </div>
            )}

            {/* Pestañas */}
            <div className="flex gap-1 border-b border-slate-200 mb-4">
              {[
                { id: 'pago', label: 'Datos de Pago', icon: DollarSign },
                { id: 'iva', label: 'Retención IVA', icon: FileText, activo: formValidacion.esAgenteRetencionIVA,
                  completo: formValidacion.numeroRetencionIVA && formValidacion.fechaRetencionIVA && formValidacion.montoRetencionIVA },
                { id: 'islr', label: 'Retención ISLR', icon: FileText, activo: formValidacion.esServicioConISLR,
                  completo: formValidacion.numeroRetencionISLR && formValidacion.fechaRetencionISLR && formValidacion.montoRetencionISLR },
              ].map(tab => {
                const Icon = tab.icon;
                const seleccionada = tabValidacionPago === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setTabValidacionPago(tab.id)}
                    className={`px-4 py-2.5 text-sm font-medium flex items-center gap-2 border-b-2 -mb-px transition-colors ${
                      seleccionada
                        ? 'border-slate-700 text-slate-800'
                        : 'border-transparent text-slate-400 hover:text-slate-600'
                    }`}
                  >
                    <Icon size={14} />
                    {tab.label}
                    {tab.activo && (
                      <span className={`w-1.5 h-1.5 rounded-full ${tab.completo ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                    )}
                  </button>
                );
              })}
            </div>

            <fieldset disabled={bloqueadoPorConciliacion} className="space-y-4 border-0 p-0 m-0 min-w-0">
              {tabValidacionPago === 'pago' && <>
              {/* Configuración de Factura y Anticipo */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-200 space-y-4">
                <h4 className="text-sm font-bold text-blue-800 flex items-center gap-2">
                  <FileText size={16} />
                  Configuración de Factura y Anticipo
                </h4>
                <div className="flex items-center gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formValidacion.requiere_factura}
                      onChange={(e) => setFormValidacion(prev => ({
                        ...prev,
                        requiere_factura: e.target.checked,
                        ...(!e.target.checked && { requiere_anticipo: false, anticipo_monto_usd: '', anticipo_tasa_cambio: '' })
                      }))}
                      className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-slate-700">¿Requiere factura?</span>
                  </label>
                  {formValidacion.requiere_factura && (
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formValidacion.requiere_anticipo}
                        onChange={(e) => setFormValidacion(prev => ({
                          ...prev,
                          requiere_anticipo: e.target.checked,
                          ...(!e.target.checked && { anticipo_monto_usd: '', anticipo_tasa_cambio: '' })
                        }))}
                        className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
                      />
                      <span className="text-sm font-medium text-slate-700">¿Lleva anticipo?</span>
                    </label>
                  )}
                </div>
                {formValidacion.requiere_factura && formValidacion.requiere_anticipo && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-white rounded-lg p-4 border border-purple-200">
                    <div>
                      <label className="block text-sm font-medium text-purple-700 mb-1">
                        Monto Anticipo (USD) *
                      </label>
                      <input
                        type="number"
                        value={formValidacion.anticipo_monto_usd}
                        onChange={(e) => setFormValidacion(prev => ({ ...prev, anticipo_monto_usd: e.target.value }))}
                        step="0.01"
                        min="0"
                        className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        placeholder="Ej: 500.00"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-purple-700 mb-1">
                        Tasa de Cambio *
                      </label>
                      <input
                        type="number"
                        value={formValidacion.anticipo_tasa_cambio}
                        onChange={(e) => setFormValidacion(prev => ({ ...prev, anticipo_tasa_cambio: e.target.value }))}
                        step="0.01"
                        min="0"
                        className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        placeholder="Ej: 36.50"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Quien paga -- ya no se pregunta: es el cliente al que se le factura */}
              <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Quién paga / cancela</label>
                  <div className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-700">
                    {formValidacion.cliente_nombre || 'Sin cliente asociado'}
                  </div>
                  <p className={`text-xs mt-1 font-medium ${formValidacion.cliente_agente_retencion ? 'text-orange-600' : 'text-slate-500'}`}>
                    {formValidacion.cliente_agente_retencion
                      ? '⚠️ Registrado como agente de retención de IVA'
                      : 'No está registrado como agente de retención'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Cuenta bancaria de Gaman</label>
                  <select
                    value={formValidacion.cuenta_bancaria_id}
                    onChange={(e) => setFormValidacion(prev => ({ ...prev, cuenta_bancaria_id: e.target.value }))}
                    className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 focus:ring-2 focus:ring-slate-500/20 focus:border-slate-500 focus:outline-none transition-all shadow-sm"
                  >
                    <option value="">— Sin especificar —</option>
                    {cuentasBancarias.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.alias} ({c.banco_nombre} · {c.numero_cuenta}, {c.moneda})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Datos Básicos - 3 columnas */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Referencia Bancaria */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Referencia Bancaria <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formValidacion.referencia}
                    onChange={(e) => setFormValidacion(prev => ({ ...prev, referencia: e.target.value }))}
                    placeholder="Ej: 1234567890"
                    className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 focus:ring-2 focus:ring-slate-500/20 focus:border-slate-500 focus:outline-none transition-all shadow-sm"
                  />
                </div>

                {/* Fecha */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Fecha <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formValidacion.fecha}
                    onChange={(e) => setFormValidacion(prev => ({ ...prev, fecha: e.target.value }))}
                    className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 focus:ring-2 focus:ring-slate-500/20 focus:border-slate-500 focus:outline-none transition-all shadow-sm"
                  />
                </div>

                {/* Monto + Moneda (A4: antes no se distinguia en que moneda pago el cliente) */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Monto <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formValidacion.monto}
                      onChange={(e) => setFormValidacion(prev => ({ ...prev, monto: e.target.value }))}
                      placeholder="0.00"
                      className="flex-1 min-w-0 px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 focus:ring-2 focus:ring-slate-500/20 focus:border-slate-500 focus:outline-none transition-all shadow-sm"
                    />
                    <select
                      value={formValidacion.moneda}
                      onChange={(e) => setFormValidacion(prev => ({ ...prev, moneda: e.target.value, tasa_cambio: e.target.value === 'USD' ? '' : prev.tasa_cambio }))}
                      className="w-24 px-2 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 focus:ring-2 focus:ring-slate-500/20 focus:border-slate-500 focus:outline-none transition-all shadow-sm"
                    >
                      <option value="USD">USD</option>
                      <option value="VES">Bs</option>
                    </select>
                  </div>
                </div>

                {formValidacion.moneda === 'VES' && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Tasa de cambio (BCV) <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        step="0.0001"
                        min="0"
                        value={formValidacion.tasa_cambio}
                        onChange={(e) => setFormValidacion(prev => ({ ...prev, tasa_cambio: e.target.value }))}
                        placeholder="0.0000"
                        className="flex-1 min-w-0 px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 focus:ring-2 focus:ring-slate-500/20 focus:border-slate-500 focus:outline-none transition-all shadow-sm"
                      />
                      <button
                        type="button"
                        onClick={handleUsarTasaBcvPago}
                        disabled={buscandoTasa}
                        className="px-3 py-2 border border-slate-300 rounded-xl text-sm text-slate-600 hover:bg-slate-50 flex items-center gap-1 whitespace-nowrap disabled:opacity-50"
                      >
                        {buscandoTasa ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Tasa BCV de hoy
                      </button>
                    </div>
                    {formValidacion.tasa_cambio > 0 && formValidacion.monto > 0 && (
                      <p className="text-xs text-slate-500 mt-1">
                        Esperado $ {(parseFloat(formValidacion.monto) / parseFloat(formValidacion.tasa_cambio)).toFixed(2)} · En cuenta Bs {parseFloat(formValidacion.monto).toFixed(2)} · Tasa {formValidacion.tasa_cambio}
                      </p>
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Cuenta receptora <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formValidacion.cuenta_bancaria_id}
                    onChange={(e) => setFormValidacion(prev => ({ ...prev, cuenta_bancaria_id: e.target.value }))}
                    className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 focus:ring-2 focus:ring-slate-500/20 focus:border-slate-500 focus:outline-none transition-all shadow-sm"
                    required
                  >
                    <option value="">Seleccione la cuenta de Gaman</option>
                    {cuentasBancarias.map(cuenta => (
                      <option key={cuenta.id} value={cuenta.id}>
                        {cuenta.alias} - {cuenta.banco_nombre || cuenta.banco_codigo} ({cuenta.moneda})
                      </option>
                    ))}
                  </select>
                  {cuentasBancarias.length === 0 && (
                    <p className="text-xs text-amber-600 mt-1">Registre primero una cuenta propia en Conciliación Bancaria.</p>
                  )}
                </div>
              </div>

              {/* Captura de Pago (Opcional) */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Captura de Pago <span className="text-slate-400 text-xs">(Opcional)</span>
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm file:mr-3 file:py-1.5 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200 transition-all cursor-pointer"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Formatos: JPG, PNG. Máximo: 5MB
                </p>
                {formValidacion.captura && (
                  <p className="text-xs text-emerald-600 mt-1">
                    ✓ Archivo seleccionado: {formValidacion.captura.name}
                  </p>
                )}
                {validacionesPago[modalValidacionPago.cotizacionId]?.capturaNombre && !formValidacion.captura && (
                  <p className="text-xs text-slate-600 mt-1">
                    📎 Archivo guardado anteriormente: {validacionesPago[modalValidacionPago.cotizacionId].capturaNombre}
                  </p>
                )}
              </div>
              </>}

              {tabValidacionPago === 'iva' && (
                /* ========== SECCIÓN 1: Retención IVA ========== */
                <div className="bg-slate-50/80 rounded-xl p-5 border border-slate-200/80 transition-all duration-300">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                      <FileText size={16} className="text-slate-500" />
                      ¿Es Agente de Retención IVA?
                      {/* Tooltip informativo */}
                      <div className="group relative">
                        <HelpCircle size={14} className="text-slate-400 cursor-help" />
                        <div className="hidden group-hover:block absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-2 bg-slate-800 text-white text-xs rounded-lg z-50">
                          Aplica cuando el cliente es agente de retención de IVA y debe retener el 15% del IVA facturado
                          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800"></div>
                        </div>
                      </div>
                    </h4>
                    {/* Indicador de progreso */}
                    {formValidacion.esAgenteRetencionIVA && (
                      <span className="text-xs px-2 py-1 bg-slate-100 text-slate-700 rounded-full">
                        {formValidacion.numeroRetencionIVA && formValidacion.fechaRetencionIVA && formValidacion.montoRetencionIVA ? '✓ Completo' : '○ Incompleto'}
                      </span>
                    )}
                  </div>

                  {!facturaResuelta && (
                    <div className="mb-3 p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
                      Todavía no hay una factura emitida para esta cotización. La retención IVA se calcula sobre el IVA que esa factura congela al emitirse, así que se habilita cuando exista.
                    </div>
                  )}

                  {/* Checkbox: Es Agente de Retención IVA */}
                  <div className="mb-3">
                    <label className={`flex items-center gap-3 p-3 bg-white/80 rounded-xl border border-slate-300 transition-all duration-200 ${facturaResuelta ? 'cursor-pointer hover:bg-slate-50' : 'opacity-50 cursor-not-allowed'}`}>
                      <input
                        type="checkbox"
                        checked={formValidacion.esAgenteRetencionIVA}
                        disabled={!facturaResuelta}
                        onChange={(e) => setFormValidacion(prev => ({
                          ...prev,
                          esAgenteRetencionIVA: e.target.checked,
                          ...(!e.target.checked && { numeroRetencionIVA: '', fechaRetencionIVA: '', montoRetencionIVA: '', capturaRetencionIVA: null })
                        }))}
                        className="w-5 h-5 text-slate-600 rounded focus:ring-slate-500"
                      />
                      <span className="text-sm font-medium text-slate-700">
                        Marcar si quien paga/cancela es agente de retención IVA
                      </span>
                    </label>
                  </div>

                  {/* Campos de retención IVA (solo si está marcado) */}
                  {formValidacion.esAgenteRetencionIVA && (
                    <div className="space-y-3 pl-2 border-l-2 border-slate-300 animate-in fade-in slide-in-from-top-2 duration-300">
                      {/* Cálculo automático sugerido */}
                      <div className="p-2 bg-slate-100 rounded text-xs text-slate-600">
                        💡 <strong>Sugerencia:</strong> La retención de IVA es típicamente el 15% del IVA total (ej: si IVA=$16, retención=$2.40)
                      </div>

                      <div className="grid grid-cols-1 gap-3">
                        {/* Número de Retención IVA */}
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">
                            Número de Retención IVA <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={formValidacion.numeroRetencionIVA}
                            onChange={(e) => setFormValidacion(prev => ({ ...prev, numeroRetencionIVA: e.target.value }))}
                            placeholder="Ej: 2024001234567"
                            className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-slate-500/20 focus:border-slate-500 focus:outline-none transition-all shadow-sm"
                          />
                        </div>

                        {/* Fecha de Retención IVA */}
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">
                            Fecha de Retención IVA <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="date"
                            value={formValidacion.fechaRetencionIVA}
                            onChange={(e) => setFormValidacion(prev => ({ ...prev, fechaRetencionIVA: e.target.value }))}
                            className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-slate-500/20 focus:border-slate-500 focus:outline-none transition-all shadow-sm"
                          />
                        </div>

                        {/* Monto de Retención IVA con cálculo sugerido */}
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">
                            Monto de Retención IVA <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={formValidacion.montoRetencionIVA}
                            onChange={(e) => setFormValidacion(prev => ({ ...prev, montoRetencionIVA: e.target.value }))}
                            placeholder="0.00"
                            className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-slate-500/20 focus:border-slate-500 focus:outline-none transition-all shadow-sm"
                          />
                          {!formValidacion.montoRetencionIVA && (() => {
                            // El IVA sale del que la factura real ya congelo al
                            // emitirse (fn_emitir_factura), no se recalcula aqui.
                            // El % de retencion es el del cliente (75% o 100%,
                            // clientes.porcentaje_retencion) -- nunca un numero
                            // fijo en el codigo.
                            const ivaReal = parseFloat(facturaResuelta?.impuesto);
                            const pctRetencion = parseFloat(facturaResuelta?.cliente_porcentaje_retencion);
                            if (!Number.isFinite(ivaReal) || !Number.isFinite(pctRetencion)) {
                              return (
                                <p className="mt-1 text-xs text-amber-600">
                                  Falta el % de retención IVA del cliente (Clientes → editar) para poder sugerir un monto.
                                </p>
                              );
                            }
                            const retencion = ivaReal * pctRetencion;
                            return (
                              <button
                                type="button"
                                onClick={() => setFormValidacion(prev => ({ ...prev, montoRetencionIVA: retencion.toFixed(2) }))}
                                className="mt-1 text-xs text-slate-600 hover:text-slate-800 underline"
                              >
                                💡 Calcular automáticamente ({(pctRetencion * 100).toFixed(0)}% del IVA de la factura: {fmtMonto(ivaReal, facturaResuelta?.moneda)})
                              </button>
                            );
                          })()}
                        </div>

                        {/* Captura de Retención IVA (Opcional) con previsualización */}
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">
                            Captura de Retención IVA <span className="text-slate-400 text-xs">(Opcional)</span>
                          </label>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleFileChangeRetencionIVA}
                            className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm file:mr-3 file:py-1.5 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200 transition-all cursor-pointer"
                          />
                          <p className="text-xs text-slate-500 mt-1">
                            Formatos: JPG, PNG. Máximo: 5MB
                          </p>
                          {/* Previsualización de imagen */}
                          {formValidacion.capturaRetencionIVA && (
                            <div className="mt-2 p-2 bg-slate-100 rounded-lg">
                              <div className="flex items-center gap-2">
                                <div 
                                  className="w-12 h-12 rounded-lg bg-cover bg-center border border-slate-300 cursor-pointer hover:opacity-80 transition-opacity"
                                  style={{ 
                                    backgroundImage: `url(${URL.createObjectURL(formValidacion.capturaRetencionIVA)})`,
                                    backgroundSize: 'cover'
                                  }}
                                  onClick={() => window.open(URL.createObjectURL(formValidacion.capturaRetencionIVA), '_blank')}
                                  title="Click para ver en tamaño completo"
                                />
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs text-slate-600 truncate">
                                    ✓ {formValidacion.capturaRetencionIVA.name}
                                  </p>
                                  <p className="text-xs text-slate-400">
                                    {(formValidacion.capturaRetencionIVA.size / 1024).toFixed(1)} KB
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                          {validacionesPago[modalValidacionPago?.cotizacionId]?.capturaRetencionIVANombre && !formValidacion.capturaRetencionIVA && (
                            <p className="text-xs text-slate-500 mt-1">
                              📎 Archivo guardado: {validacionesPago[modalValidacionPago.cotizacionId].capturaRetencionIVANombre}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {tabValidacionPago === 'islr' && (
                /* ========== SECCIÓN 2: Servicio con ISLR ========== */
                <div className="bg-zinc-50/80 rounded-xl p-5 border border-zinc-200/80 transition-all duration-300">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-zinc-700 flex items-center gap-2">
                      <FileText size={16} className="text-zinc-500" />
                      ¿Es un Servicio con ISLR?
                      {/* Tooltip informativo */}
                      <div className="group relative">
                        <HelpCircle size={14} className="text-zinc-400 cursor-help" />
                        <div className="hidden group-hover:block absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-2 bg-zinc-800 text-white text-xs rounded-lg z-50">
                          ISLR aplica a pagos por servicios profesionales. El % varía según el tipo de persona (PN 1-3%, PJ 2-5%)
                          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-zinc-800"></div>
                        </div>
                      </div>
                    </h4>
                    {/* Indicador de progreso */}
                    {formValidacion.esServicioConISLR && (
                      <span className="text-xs px-2 py-1 bg-slate-100 text-slate-700 rounded-full">
                        {formValidacion.numeroRetencionISLR && formValidacion.fechaRetencionISLR && formValidacion.montoRetencionISLR ? '✓ Completo' : '○ Incompleto'}
                      </span>
                    )}
                  </div>

                  {!facturaResuelta && (
                    <div className="mb-3 p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
                      Todavía no hay una factura emitida para esta cotización. La retención ISLR se calcula sobre la base que esa factura congela al emitirse, así que se habilita cuando exista.
                    </div>
                  )}

                  {/* Checkbox: Es Servicio con ISLR */}
                  <div className="mb-3">
                    <label className={`flex items-center gap-3 p-3 bg-white/80 rounded-xl border border-zinc-300 transition-all duration-200 ${facturaResuelta ? 'cursor-pointer hover:bg-zinc-50' : 'opacity-50 cursor-not-allowed'}`}>
                      <input
                        type="checkbox"
                        checked={formValidacion.esServicioConISLR}
                        disabled={!facturaResuelta}
                        onChange={(e) => setFormValidacion(prev => ({
                          ...prev,
                          esServicioConISLR: e.target.checked,
                          ...(!e.target.checked && { numeroRetencionISLR: '', fechaRetencionISLR: '', montoRetencionISLR: '', capturaRetencionISLR: null })
                        }))}
                        className="w-5 h-5 text-zinc-600 rounded focus:ring-zinc-500"
                      />
                      <span className="text-sm font-medium text-zinc-700">
                        Marcar si es un servicio que requiere retención ISLR
                      </span>
                    </label>
                  </div>

                  {/* Campos de ISLR (solo si está marcado) */}
                  {formValidacion.esServicioConISLR && (
                    <div className="space-y-3 pl-2 border-l-2 border-zinc-300 animate-in fade-in slide-in-from-top-2 duration-300">
                      {/* Info de porcentajes ISLR */}
                      <div className="p-2 bg-zinc-100 rounded text-xs text-zinc-600">
                        💡 <strong>Porcentajes ISLR:</strong> Persona Natural 1-3% | Persona Jurídica 2-5% según concepto
                      </div>

                      <div className="grid grid-cols-1 gap-3">
                        {/* Número de Retención ISLR */}
                        <div>
                          <label className="block text-sm font-medium text-zinc-700 mb-1">
                            Número de Retención ISLR <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={formValidacion.numeroRetencionISLR}
                            onChange={(e) => setFormValidacion(prev => ({ ...prev, numeroRetencionISLR: e.target.value }))}
                            placeholder="Ej: ISLR-2024001234"
                            className="w-full px-3 py-2.5 border border-zinc-200 rounded-xl text-sm focus:ring-2 focus:ring-zinc-500/20 focus:border-zinc-500 focus:outline-none transition-all shadow-sm"
                          />
                        </div>

                        {/* Fecha de Retención ISLR */}
                        <div>
                          <label className="block text-sm font-medium text-zinc-700 mb-1">
                            Fecha de Retención ISLR <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="date"
                            value={formValidacion.fechaRetencionISLR}
                            onChange={(e) => setFormValidacion(prev => ({ ...prev, fechaRetencionISLR: e.target.value }))}
                            className="w-full px-3 py-2.5 border border-zinc-200 rounded-xl text-sm focus:ring-2 focus:ring-zinc-500/20 focus:border-zinc-500 focus:outline-none transition-all shadow-sm"
                          />
                        </div>

                        {/* Monto de Retención ISLR con cálculo sugerido */}
                        <div>
                          <label className="block text-sm font-medium text-zinc-700 mb-1">
                            Monto de Retención ISLR <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={formValidacion.montoRetencionISLR}
                            onChange={(e) => setFormValidacion(prev => ({ ...prev, montoRetencionISLR: e.target.value }))}
                            placeholder="0.00"
                            className="w-full px-3 py-2.5 border border-zinc-200 rounded-xl text-sm focus:ring-2 focus:ring-zinc-500/20 focus:border-zinc-500 focus:outline-none transition-all shadow-sm"
                          />
                          {!formValidacion.montoRetencionISLR && (() => {
                            // Base ISLR = lo mismo que usa fn_emitir_factura /
                            // Retencion.crearRecibidaDesdeFactura: subtotal +
                            // base_exenta (nunca el monto pagado). El % sale
                            // del concepto ISLR asignado al cliente
                            // (clientes.islr_concepto_id), pn o pj segun
                            // corresponda -- nunca un numero fijo aqui.
                            const concepto = islrConceptosReales.find(c => c.id === facturaResuelta?.cliente_islr_concepto_id);
                            const tipoPersona = facturaResuelta?.cliente_tipo_persona;
                            const pct = concepto && tipoPersona
                              ? parseFloat(tipoPersona === 'juridica' ? concepto.porcentaje_pj : concepto.porcentaje_pn)
                              : null;
                            const base = facturaResuelta
                              ? (parseFloat(facturaResuelta.subtotal) || 0) + (parseFloat(facturaResuelta.base_exenta) || 0)
                              : null;
                            if (!concepto || !Number.isFinite(pct) || !Number.isFinite(base)) {
                              return (
                                <p className="mt-1 text-xs text-amber-600">
                                  Este cliente no tiene un concepto ISLR asignado (Clientes → editar) para poder sugerir un monto.
                                </p>
                              );
                            }
                            const retencion = base * pct;
                            return (
                              <button
                                type="button"
                                onClick={() => setFormValidacion(prev => ({ ...prev, montoRetencionISLR: retencion.toFixed(2) }))}
                                className="text-xs text-zinc-600 hover:text-zinc-800 underline block mt-1"
                              >
                                💡 Calcular ({concepto.nombre}, {tipoPersona === 'juridica' ? 'PJ' : 'PN'} {(pct * 100).toFixed(0)}%)
                              </button>
                            );
                          })()}
                        </div>

                        {/* Captura de Retención ISLR (Opcional) con previsualización */}
                        <div>
                          <label className="block text-sm font-medium text-zinc-700 mb-1">
                            Captura de Retención ISLR <span className="text-zinc-400 text-xs">(Opcional)</span>
                          </label>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleFileChangeRetencionISLR}
                            className="w-full px-3 py-2.5 border border-zinc-200 rounded-xl text-sm file:mr-3 file:py-1.5 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-zinc-100 file:text-zinc-700 hover:file:bg-zinc-200 transition-all cursor-pointer"
                          />
                          <p className="text-xs text-zinc-500 mt-1">
                            Formatos: JPG, PNG. Máximo: 5MB
                          </p>
                          {/* Previsualización de imagen */}
                          {formValidacion.capturaRetencionISLR && (
                            <div className="mt-2 p-2 bg-zinc-100 rounded-lg">
                              <div className="flex items-center gap-2">
                                <div 
                                  className="w-12 h-12 rounded-lg bg-cover bg-center border border-zinc-300 cursor-pointer hover:opacity-80 transition-opacity"
                                  style={{ 
                                    backgroundImage: `url(${URL.createObjectURL(formValidacion.capturaRetencionISLR)})`,
                                    backgroundSize: 'cover'
                                  }}
                                  onClick={() => window.open(URL.createObjectURL(formValidacion.capturaRetencionISLR), '_blank')}
                                  title="Click para ver en tamaño completo"
                                />
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs text-slate-600 truncate">
                                    ✓ {formValidacion.capturaRetencionISLR.name}
                                  </p>
                                  <p className="text-xs text-zinc-400">
                                    {(formValidacion.capturaRetencionISLR.size / 1024).toFixed(1)} KB
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                          {validacionesPago[modalValidacionPago?.cotizacionId]?.capturaRetencionISLRNombre && !formValidacion.capturaRetencionISLR && (
                            <p className="text-xs text-zinc-500 mt-1">
                              📎 Archivo guardado: {validacionesPago[modalValidacionPago.cotizacionId].capturaRetencionISLRNombre}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ========== RESUMEN DE RETENCIONES (siempre visible, cualquiera sea la pestaña) ========== */}
              {(formValidacion.esAgenteRetencionIVA || formValidacion.esServicioConISLR) && (
                <div className="mt-4 p-5 bg-gradient-to-r from-slate-50 to-zinc-50 rounded-xl border border-slate-200/80">
                  <h5 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <Calculator size={16} />
                    Resumen de Retenciones
                  </h5>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div className="p-3 bg-white rounded-xl border border-slate-200/80 shadow-sm">
                      <p className="text-slate-500 text-xs">Monto Original</p>
                      <p className="font-semibold text-slate-700">
                        {parseFloat(formValidacion.monto || 0).toFixed(2)}
                      </p>
                    </div>
                    {formValidacion.esAgenteRetencionIVA && (
                      <div className="p-3 bg-white rounded-xl border border-slate-200/80 shadow-sm">
                        <p className="text-slate-500 text-xs">Retención IVA</p>
                        <p className="font-semibold text-slate-700">
                          -{parseFloat(formValidacion.montoRetencionIVA || 0).toFixed(2)}
                        </p>
                      </div>
                    )}
                    {formValidacion.esServicioConISLR && (
                      <div className="p-3 bg-white rounded-xl border border-zinc-200/80 shadow-sm">
                        <p className="text-zinc-500 text-xs">Retención ISLR</p>
                        <p className="font-semibold text-zinc-700">
                          -{parseFloat(formValidacion.montoRetencionISLR || 0).toFixed(2)}
                        </p>
                      </div>
                    )}
                    <div className="p-3 bg-slate-100 rounded-xl border border-slate-200/80">
                      <p className="text-slate-600 text-xs">Neto a Pagar</p>
                      <p className="font-bold text-slate-700">
                        {(
                          parseFloat(formValidacion.monto || 0) -
                          parseFloat(formValidacion.montoRetencionIVA || 0) -
                          parseFloat(formValidacion.montoRetencionISLR || 0)
                        ).toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </fieldset>

            {/* Botones */}
            <div className="mt-6 flex gap-3">
              <button 
                onClick={cerrarModalValidacionPago}
                className="flex-1 py-2.5 px-4 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-sm font-medium transition-all duration-200 active:scale-95"
              >
                Cancelar
              </button>
              {!bloqueadoPorConciliacion && (
                <button
                  onClick={guardarValidacionPago}
                  disabled={guardandoValidacion}
                  className="flex-1 py-2.5 px-4 bg-slate-700 hover:bg-slate-800 text-white rounded-xl text-sm font-medium transition-all duration-200 active:scale-95 shadow-sm disabled:opacity-50"
                >
                  {guardandoValidacion ? 'Guardando…' : 'Guardar Validación'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      );
    })()}

    {/* Modal para aplicar ISLR */}
    {modalISLR && (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full border border-slate-200/80">
          <div className="p-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-1">Aplicar Concepto ISLR</h3>
            <p className="text-xs text-blue-600 mb-4 bg-blue-50 px-3 py-2 rounded-lg">
              Cliente: <span className="font-semibold">{modalISLR.tipoPersona === 'juridica' ? 'Persona Jurídica (PJ)' : 'Persona Natural (PN)'}</span>
              <span className="block text-slate-500 mt-1">El % de retención se calcula automáticamente según el tipo de persona</span>
            </p>
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {/* Opción Sin ISLR */}
              <button
                onClick={() => handleCambiarISLR(modalISLR.facturaId, { id: null, codigo: null, porcentaje: 0 })}
                disabled={updatingId === modalISLR.facturaId}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all duration-200 ${
                  modalISLR.porcentajeActual === 0
                    ? 'bg-slate-100 font-semibold cursor-default ring-2 ring-slate-300'
                    : 'hover:bg-slate-50'
                }`}
              >
                <span className="w-4 h-4 rounded-full bg-slate-400"></span>
                <div className="flex-1">
                  <span className="text-sm font-medium">Sin ISLR</span>
                  <p className="text-xs text-slate-400">No aplica retención (0%)</p>
                </div>
                {modalISLR.porcentajeActual === 0 && (
                  <span className="text-xs text-emerald-600 font-medium">Actual</span>
                )}
              </button>

              <div className="border-t border-slate-200 my-2"></div>

              {/* Conceptos reales (islr_conceptos), con el % del tipo de
                  persona correspondiente -- ya es fraccion (0.03 = 3%),
                  no hay que convertir nada aqui. */}
              {islrConceptosReales.map((concepto, idx) => {
                const pct = parseFloat(modalISLR.tipoPersona === 'juridica' ? concepto.porcentaje_pj : concepto.porcentaje_pn);
                const colores = ['bg-red-500', 'bg-orange-500', 'bg-yellow-500', 'bg-green-500', 'bg-blue-500', 'bg-purple-500', 'bg-pink-500', 'bg-teal-500', 'bg-indigo-500', 'bg-cyan-500'];
                return (
                <button
                  key={concepto.id}
                  onClick={() => handleCambiarISLR(modalISLR.facturaId, {
                    id: concepto.id,
                    codigo: concepto.codigo,
                    label: concepto.nombre,
                    porcentaje: pct
                  })}
                  disabled={updatingId === modalISLR.facturaId}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all duration-200 ${
                    modalISLR.conceptoCodigo === concepto.codigo
                      ? 'bg-slate-100 font-semibold cursor-default ring-2 ring-slate-300'
                      : 'hover:bg-slate-50'
                  }`}
                >
                  <span className={`w-4 h-4 rounded-full ${colores[idx % colores.length]}`}></span>
                  <div className="flex-1">
                    <span className="text-sm font-medium">{concepto.nombre}</span>
                  </div>
                  <span className="text-sm font-bold text-slate-700">{Math.round(pct * 100)}%</span>
                  {modalISLR.conceptoCodigo === concepto.codigo && (
                    <span className="text-xs text-emerald-600 font-medium ml-2">Actual</span>
                  )}
                </button>
                );
              })}
            </div>
            <button
              onClick={() => setModalISLR(null)}
              className="mt-6 w-full py-2.5 px-4 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-sm font-medium transition-all duration-200 active:scale-95"
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Modal para imprimir Nota de Entrega */}
    {imprimirNotaCompra && (
      <NotaCompraPrintModal
        compra={imprimirNotaCompra}
        onClose={() => setImprimirNotaCompra(null)}
        onImprimir={(compraId) => {
          // Cambiar el estado a 'nota_entrega_lista' cuando se imprime
          setComprasPendientes(prev => prev.map(c =>
            c.id === compraId
              ? { ...c, estado: 'nota_entrega_lista', fecha_actualizacion: new Date().toISOString() }
              : c
          ));
        }}
      />
    )}

    {/* Modal para agregar/editar compra */}
    {modalCompra && (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto flex flex-col border border-slate-200/80">
          <div className="sticky top-0 bg-white/95 backdrop-blur-sm border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
            <h3 className="text-lg font-bold text-slate-800">
              {modalCompra === 'nueva' ? 'Agregar Compra' : 'Editar Compra'}
            </h3>
            <button 
              onClick={cerrarModalCompra}
              className="p-2 hover:bg-slate-100 rounded-full transition-colors"
            >
              <X size={20} className="text-slate-500" />
            </button>
          </div>
          
          <div className="p-8 space-y-6 overflow-y-auto flex-1">
            {/* Producto */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Nombre del Producto *
              </label>
              <input
                type="text"
                value={formCompra.producto}
                onChange={(e) => setFormCompra(prev => ({ ...prev, producto: e.target.value }))}
                placeholder="Ej: Motor eléctrico 5HP"
                className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 focus:outline-none transition-all shadow-sm"
              />
            </div>

            {/* Descripción */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Descripción / Especificaciones
              </label>
              <textarea
                value={formCompra.descripcion}
                onChange={(e) => setFormCompra(prev => ({ ...prev, descripcion: e.target.value }))}
                placeholder="Características técnicas, modelo, marca, etc."
                rows={3}
                className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 focus:outline-none transition-all shadow-sm resize-none"
              />
            </div>

            {/* Línea separadora */}
            <hr className="border-slate-200" />

            {/* Nuevo Flujo de Pago - PASO INICIAL */}
            <div className="border border-purple-200 rounded-xl p-6 bg-purple-50/80">
              <h3 className="text-sm font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <Calculator size={16} className="text-purple-600" /> Flujo de Pago <span className="text-red-500 font-bold">*</span>
              </h3>

              {/* Pregunta: ¿El proveedor venderá? */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  ¿El proveedor venderá? *
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="proveedor_vende"
                      checked={formCompra.proveedor_vende === true}
                      onChange={() => setFormCompra(prev => ({ ...prev, proveedor_vende: true, requiere_factura: null }))}
                      className="w-4 h-4 text-purple-600 focus:ring-purple-500"
                    />
                    <span className="text-sm">Sí, venderá</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="proveedor_vende"
                      checked={formCompra.proveedor_vende === false}
                      onChange={() => {
                        setFormCompra(prev => ({ ...prev, proveedor_vende: false, requiere_factura: null }));
                        abrirModalProveedores();
                      }}
                      className="w-4 h-4 text-purple-600 focus:ring-purple-500"
                    />
                    <span className="text-sm">No, no venderá</span>
                  </label>
                </div>
              </div>

              {/* Campos que se muestran solo si el proveedor vende */}
              {formCompra.proveedor_vende === true && (
                <>
                  {/* Monto a pagar */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Monto a Pagar *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formCompra.monto_a_pagar}
                      onChange={(e) => setFormCompra(prev => ({ ...prev, monto_a_pagar: e.target.value }))}
                      placeholder="0.00"
                      className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 focus:outline-none transition-all shadow-sm"
                    />
                  </div>

                  {/* ¿Requiere factura? */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      ¿Requiere factura?
                    </label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="requiere_factura"
                          checked={formCompra.requiere_factura === true}
                          onChange={() => setFormCompra(prev => ({ ...prev, requiere_factura: true }))}
                          className="w-4 h-4 text-purple-600 focus:ring-purple-500"
                        />
                        <span className="text-sm">Con factura</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="requiere_factura"
                          checked={formCompra.requiere_factura === false}
                          onChange={() => setFormCompra(prev => ({ ...prev, requiere_factura: false }))}
                          className="w-4 h-4 text-purple-600 focus:ring-purple-500"
                        />
                        <span className="text-sm">Sin factura</span>
                      </label>
                    </div>
                  </div>

                  {/* Retención e ISLR */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        % Retención (Manual)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={formCompra.porcentaje_retencion}
                        onChange={(e) => {
                          const porcentaje = parseFloat(e.target.value) || 0;
                          const monto = parseFloat(formCompra.monto_a_pagar) || 0;
                          const montoRetencion = (monto * porcentaje) / 100;
                          setFormCompra(prev => ({
                            ...prev,
                            porcentaje_retencion: e.target.value,
                            monto_retencion_calculado: montoRetencion.toFixed(2)
                          }));
                        }}
                        placeholder="0.00"
                        className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 focus:outline-none transition-all shadow-sm"
                      />
                      {formCompra.monto_retencion_calculado && (
                        <p className="text-xs text-slate-500 mt-1">
                          Monto: {formCompra.monto_retencion_calculado}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        % ISLR (Manual)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={formCompra.porcentaje_islr}
                        onChange={(e) => {
                          const porcentaje = parseFloat(e.target.value) || 0;
                          const monto = parseFloat(formCompra.monto_a_pagar) || 0;
                          const montoISLR = (monto * porcentaje) / 100;
                          setFormCompra(prev => ({
                            ...prev,
                            porcentaje_islr: e.target.value,
                            monto_islr_calculado: montoISLR.toFixed(2)
                          }));
                        }}
                        placeholder="0.00"
                        className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 focus:outline-none transition-all shadow-sm"
                      />
                      {formCompra.monto_islr_calculado && (
                        <p className="text-xs text-slate-500 mt-1">
                          Monto: {formCompra.monto_islr_calculado}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Campos de pago */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Monto de Transferencia *
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formCompra.monto_transferencia}
                        onChange={(e) => setFormCompra(prev => ({ ...prev, monto_transferencia: e.target.value }))}
                        placeholder="0.00"
                        className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 focus:outline-none transition-all shadow-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Referencia Bancaria *
                      </label>
                      <input
                        type="text"
                        value={formCompra.referencia_bancaria}
                        onChange={(e) => setFormCompra(prev => ({ ...prev, referencia_bancaria: e.target.value }))}
                        placeholder="Ej: REF-123456"
                        className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 focus:outline-none transition-all shadow-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Fecha de Pago *
                      </label>
                      <input
                        type="date"
                        value={formCompra.fecha_pago}
                        onChange={(e) => setFormCompra(prev => ({ ...prev, fecha_pago: e.target.value }))}
                        className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 focus:outline-none transition-all shadow-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Comprobante de Pago *
                      </label>
                      <input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => setFormCompra(prev => ({ ...prev, comprobante_pago: e.target.files[0] || null }))}
                        className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm file:mr-3 file:py-1.5 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-purple-100 file:text-purple-700 hover:file:bg-purple-200 transition-all cursor-pointer"
                      />
                      {formCompra.comprobante_pago && (
                        <p className="text-xs text-emerald-600 mt-2">
                          ✓ Archivo seleccionado: {formCompra.comprobante_pago.name}
                        </p>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* Indicador de estado del flujo de pago */}
              {formCompra.proveedor_vende === false && !formCompra.proveedor_sugerido && (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                  <p className="text-sm text-amber-800">
                    ⚠️ El proveedor no venderá. Seleccione un proveedor alternativo o proveedor externo para continuar.
                  </p>
                </div>
              )}

              {formCompra.proveedor_vende === false && formCompra.proveedor_sugerido && (
                <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-xl">
                  <p className="text-sm text-emerald-800">
                    ✅ Proveedor seleccionado: {formCompra.proveedor_sugerido}. Puede continuar con los demás campos.
                  </p>
                </div>
              )}

              {formCompra.proveedor_vende === true && (
                <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-xl">
                  <p className="text-sm text-emerald-800">
                    ✅ Complete todos los campos de pago para desbloquear el resto del formulario.
                  </p>
                </div>
              )}
            </div>

            {/* Línea separadora */}
            <hr className="border-slate-200" />

            {/* Campos que se desbloquean después del flujo de pago */}
            {(formCompra.proveedor_vende === false && formCompra.proveedor_sugerido) || (formCompra.proveedor_vende === true && formCompra.monto_a_pagar && formCompra.requiere_factura !== null && formCompra.monto_transferencia && formCompra.referencia_bancaria && formCompra.fecha_pago && formCompra.comprobante_pago) ? (
              <>
                {/* Cantidad y Tipo */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Cantidad
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={formCompra.cantidad}
                      onChange={(e) => setFormCompra(prev => ({ ...prev, cantidad: parseInt(e.target.value) || 1 }))}
                      className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 focus:outline-none transition-all shadow-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Tipo de Compra
                    </label>
                    <select
                      value={formCompra.tipo}
                      onChange={(e) => setFormCompra(prev => ({ ...prev, tipo: e.target.value }))}
                      className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 focus:outline-none transition-all shadow-sm cursor-pointer"
                    >
                      <option value="nacional">🏠 Nacional</option>
                      <option value="internacional">🌍 Internacional</option>
                    </select>
                  </div>
                </div>

                {/* Proveedor sugerido */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Proveedor Sugerido
                  </label>
                  <input
                    type="text"
                    value={formCompra.proveedor_sugerido}
                    onChange={(e) => setFormCompra(prev => ({ ...prev, proveedor_sugerido: e.target.value }))}
                    placeholder="Nombre del proveedor (opcional)"
                    className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 focus:outline-none transition-all shadow-sm"
                  />
                </div>

                {/* Prioridad */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Prioridad
                  </label>
                  <select
                    value={formCompra.prioridad}
                    onChange={(e) => setFormCompra(prev => ({ ...prev, prioridad: e.target.value }))}
                    className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 focus:outline-none transition-all shadow-sm cursor-pointer"
                  >
                    <option value="baja">🟢 Baja</option>
                    <option value="media">🟡 Media</option>
                    <option value="alta">🔴 Alta</option>
                  </select>
                </div>

                {/* Línea separadora */}
                <hr className="border-slate-200" />

            {/* Cotización de origen y Cliente */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Cotización de Origen
                </label>
                <input
                  type="text"
                  value={formCompra.cotizacion_origen}
                  onChange={(e) => setFormCompra(prev => ({ ...prev, cotizacion_origen: e.target.value }))}
                  placeholder="Ej: COT-123 (opcional)"
                  className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 focus:outline-none transition-all shadow-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Cliente/Empresa
                </label>
                <input
                  type="text"
                  value={formCompra.cliente_nombre || ''}
                  disabled
                  placeholder="Desde cotización"
                  className="w-full px-3 py-2.5 bg-slate-100 border border-slate-200 rounded-xl text-sm text-slate-600 cursor-not-allowed"
                />
              </div>
            </div>

            {/* Notas */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Notas Adicionales
              </label>
              <textarea
                value={formCompra.notas}
                onChange={(e) => setFormCompra(prev => ({ ...prev, notas: e.target.value }))}
                placeholder="Información adicional, contactos, etc."
                rows={2}
                className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 focus:outline-none transition-all shadow-sm resize-none"
              />
            </div>

            {/* Línea separadora */}
            <hr className="border-slate-200" />

            {/* Tracking / Comentario de Envío */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1 flex items-center gap-2">
                <span>🚚</span> Comentario de Tracking / Envío
              </label>
              <textarea
                value={formCompra.tracking_comentario}
                onChange={(e) => setFormCompra(prev => ({ ...prev, tracking_comentario: e.target.value }))}
                placeholder="Número de guía, empresa de envío, fecha estimada de entrega, etc."
                rows={2}
                className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 focus:outline-none transition-all shadow-sm resize-none"
              />
            </div>

            {/* Línea separadora */}
            <hr className="border-slate-200" />

            {/* Número de Factura */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1 flex items-center gap-2">
                <span>🧾</span> Número de Factura
              </label>
              <input
                type="text"
                value={formCompra.factura || ''}
                onChange={(e) => setFormCompra(prev => ({ ...prev, factura: e.target.value }))}
                placeholder="Ej: FACT-001, 00001234"
                className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 focus:outline-none transition-all shadow-sm"
              />
            </div>

            {/* Línea separadora */}
            <hr className="border-slate-200" />

            {/* Sección de Factura del Proveedor */}
            <div className="border border-slate-200 rounded-xl p-6">
              <h3 className="text-sm font-semibold text-slate-800 mb-5 flex items-center gap-2">
                <FileText size={16} className="text-slate-500" /> Datos de Factura del Proveedor
              </h3>

              {/* Archivo de Factura */}
              <div className="mb-5">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Cargar Factura <span className="text-slate-500 text-xs">(Imagen o PDF)</span>
                </label>
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) => setFormCompra(prev => ({ ...prev, factura_file: e.target.files[0] || null }))}
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm file:mr-3 file:py-1.5 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200 transition-all cursor-pointer"
                />
                {formCompra.factura_file && (
                  <p className="text-xs text-emerald-600 mt-2">
                    ✓ Archivo seleccionado: {formCompra.factura_file.name}
                  </p>
                )}
              </div>

              {/* Número y Fecha de Factura */}
              <div className="grid grid-cols-2 gap-5 mb-5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    N° de Factura Proveedor
                  </label>
                  <input
                    type="text"
                    value={formCompra.factura_numero_proveedor || ''}
                    onChange={(e) => setFormCompra(prev => ({ ...prev, factura_numero_proveedor: e.target.value }))}
                    placeholder="Ej: PROV-001"
                    className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 focus:outline-none transition-all shadow-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Fecha de Factura
                  </label>
                  <input
                    type="date"
                    value={formCompra.factura_fecha || ''}
                    onChange={(e) => setFormCompra(prev => ({ ...prev, factura_fecha: e.target.value }))}
                    className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 focus:outline-none transition-all shadow-sm"
                  />
                </div>
              </div>

              {/* Base Imponible USD y VES */}
              <div className="grid grid-cols-2 gap-5 mb-5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center gap-1">
                    <DollarSign size={14} /> Base Imponible ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formCompra.base_imponible_usd || ''}
                    onChange={(e) => setFormCompra(prev => ({ ...prev, base_imponible_usd: e.target.value }))}
                    placeholder="0.00"
                    className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 focus:outline-none transition-all shadow-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center gap-1">
                    <span>Bs.</span> Base Imponible (Bs)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formCompra.base_imponible_ves || ''}
                    onChange={(e) => setFormCompra(prev => ({ ...prev, base_imponible_ves: e.target.value }))}
                    placeholder="0.00"
                    className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 focus:outline-none transition-all shadow-sm"
                  />
                </div>
              </div>

              {/* Retención */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center gap-1">
                  <Calculator size={14} /> Retención (si aplica)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formCompra.retencion_monto || ''}
                  onChange={(e) => setFormCompra(prev => ({ ...prev, retencion_monto: e.target.value }))}
                  placeholder="Monto retenido"
                  className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 focus:outline-none transition-all shadow-sm"
                />
              </div>
            </div>

            {/* Línea separadora */}
            <hr className="border-slate-200" />

            {/* Sección de Firmas para Nota de Entrega */}
            <div className="border-t border-slate-200 pt-4 mt-2">
              <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
                <span>✍️</span> Datos para la Nota de Entrega (opcional)
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {/* Firma Cliente */}
                <div>
                  <p className="text-xs font-medium text-slate-600 mb-2">Recibido por (Cliente):</p>
                  <input
                    type="text"
                    value={formCompra.receiver_name || ''}
                    onChange={(e) => setFormCompra(prev => ({ ...prev, receiver_name: e.target.value }))}
                    placeholder="Nombre del receptor"
                    className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl mb-2 text-sm text-slate-700 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 focus:outline-none transition-all shadow-sm"
                  />
                  <input
                    type="text"
                    value={formCompra.receiver_id || ''}
                    onChange={(e) => setFormCompra(prev => ({ ...prev, receiver_id: e.target.value }))}
                    placeholder="C.I. del receptor"
                    className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 focus:outline-none transition-all shadow-sm"
                  />
                </div>
                {/* Firma Entregador */}
                <div>
                  <p className="text-xs font-medium text-slate-600 mb-2">Entregado por:</p>
                  <input
                    type="text"
                    value={formCompra.deliverer_name || ''}
                    onChange={(e) => setFormCompra(prev => ({ ...prev, deliverer_name: e.target.value }))}
                    placeholder="Nombre del entregador"
                    className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl mb-2 text-sm text-slate-700 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 focus:outline-none transition-all shadow-sm"
                  />
                  <input
                    type="text"
                    value={formCompra.deliverer_id || ''}
                    onChange={(e) => setFormCompra(prev => ({ ...prev, deliverer_id: e.target.value }))}
                    placeholder="C.I. del entregador"
                    className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 focus:outline-none transition-all shadow-sm"
                  />
                </div>
              </div>
            </div>
              </>
            ) : (
              <div className="p-6 bg-slate-50 rounded-xl text-center border border-slate-200 border-dashed">
                <p className="text-sm text-slate-500">
                  🔒 Complete el flujo de pago para desbloquear el resto del formulario
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-slate-50/95 backdrop-blur-sm border-t border-slate-200 px-6 py-4 flex justify-end gap-3">
            <button
              onClick={cerrarModalCompra}
              className="px-4 py-2 text-slate-600 hover:text-slate-900 font-medium transition-all duration-200 active:scale-95"
            >
              Cancelar
            </button>
            <button
              onClick={guardarCompra}
              className="flex items-center gap-2 px-6 py-2.5 bg-purple-600 text-white rounded-xl hover:bg-purple-700 font-medium transition-all duration-200 shadow-sm active:scale-95"
            >
              <Save size={18} />
              {modalCompra === 'nueva' ? 'Guardar' : 'Actualizar'}
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Modal de Sugerencia de Proveedores */}
    {modalProveedores && (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[80vh] overflow-y-auto border border-slate-200/80">
          <div className="p-6 border-b border-slate-200">
            <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
              <Building size={24} className="text-purple-600" />
              Sugerencia de Proveedores
            </h2>
            <p className="text-sm text-slate-500 mt-2">
              El proveedor actual no venderá. Seleccione un proveedor alternativo para cotizar.
            </p>
          </div>

          <div className="p-6">
            {listaProveedores.length > 0 ? (
              <div className="space-y-3">
                {listaProveedores.map((proveedor) => (
                  <div
                    key={proveedor.id}
                    onClick={() => seleccionarProveedor(proveedor)}
                    className="p-4 border border-slate-200 rounded-xl hover:border-purple-500 hover:bg-purple-50/80 cursor-pointer transition-all duration-200 shadow-sm hover:shadow-md"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-slate-800">{proveedor.nombre}</h3>
                        {proveedor.rif && (
                          <p className="text-sm text-slate-500">RIF: {proveedor.rif}</p>
                        )}
                      </div>
                      <CheckCircle size={20} className="text-purple-600" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-slate-500">No hay proveedores disponibles</p>
              </div>
            )}

            {/* Botón para proveedor externo */}
            <div className="mt-4 pt-4 border-t border-slate-200">
              <button
                onClick={seleccionarProveedorExterno}
                className="w-full p-4 border-2 border-dashed border-slate-300 rounded-xl hover:border-purple-500 hover:bg-purple-50/80 cursor-pointer transition-all duration-200 flex items-center justify-center gap-2 text-slate-500 hover:text-purple-600"
              >
                <ExternalLink size={20} />
                <span className="font-medium">Proveedor Externo</span>
              </button>
            </div>
          </div>

          <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
            <button
              onClick={() => setModalProveedores(false)}
              className="px-4 py-2 text-slate-600 hover:text-slate-900 font-medium transition-all duration-200 active:scale-95"
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Modal de Vista Previa - Comprobante Retención IVA */}
    {modalVistaPreviaRetIVA && (
      <ComprobanteRetencionIVAPrintModal
        data={recaudosData}
        onClose={() => setModalVistaPreviaRetIVA(null)}
      />
    )}

    {/* Modal de Vista Previa - Comprobante Retención ISLR */}
    {modalISLRComprobante && (
      <ComprobanteISLRPrintModal
        data={modalISLRComprobante}
        onClose={() => setModalISLRComprobante(null)}
      />
    )}

    {/* Modal de Anticipo - Tabla Comparativa Mixta */}
    {modalAnticipo && (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl mx-4 max-h-[80vh] overflow-y-auto border border-purple-200/80">
          <div className="p-6 border-b border-purple-200 bg-gradient-to-r from-purple-50 to-indigo-50">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-purple-800 flex items-center gap-2">
                <DollarSign size={24} className="text-purple-600" />
                Detalle de Anticipo
              </h2>
              <button
                onClick={() => setModalAnticipo(null)}
                className="p-2 hover:bg-purple-100 rounded-lg transition-colors"
              >
                <X size={20} className="text-purple-600" />
              </button>
            </div>
            <p className="text-sm text-purple-600 mt-2">
              Factura: {modalAnticipo.factura.cotizacion_numero || modalAnticipo.factura.id}
            </p>
          </div>

          <div className="p-6">
            <AnticipoMixedTable
              anticipoData={modalAnticipo.anticipoData || modalAnticipo.factura}
              totalFactura={modalAnticipo.factura.total}
              tasaActual={tasaCambioActual}
            />
          </div>

          <div className="p-6 border-t border-purple-200 flex justify-end gap-3 bg-purple-50/50">
            <button
              onClick={() => setModalAnticipo(null)}
              className="px-6 py-2.5 bg-purple-600 text-white rounded-xl hover:bg-purple-700 font-medium transition-all duration-200 shadow-sm active:scale-95"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    )}

    {cotizacionDetalle && (
      <DetalleCotizacionModal
        cotizacion={cotizacionDetalle}
        onClose={() => setCotizacionDetalle(null)}
      />
    )}
    {ModalRenderer}
  </div>
);
};

const DetalleCotizacionModal = ({ cotizacion, onClose }) => {
  const articulos = cotizacion.articulos || cotizacion.items || [];
  const cliente = cotizacion.cliente_nombre || cotizacion.empresa_nombre || 'Sin cliente';

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto" onClick={(event) => event.stopPropagation()}>
        <div className="p-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-xl font-bold text-slate-800">Detalle de cotización</h2>
            <p className="text-sm text-slate-500 mt-1">{cotizacion.numero || `COT-${cotizacion.id}`}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700" aria-label="Cerrar detalle">
            <X size={20} />
          </button>
        </div>

        <div className="p-5 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
              <p className="text-xs text-slate-500">Cliente / Empresa</p>
              <p className="font-semibold text-slate-800 mt-1">{cliente}</p>
              {cotizacion.cliente_rif && <p className="text-xs text-slate-500 mt-1">{cotizacion.cliente_rif}</p>}
            </div>
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
              <p className="text-xs text-slate-500">Estado</p>
              <p className="font-semibold text-slate-800 mt-1">{String(cotizacion.estado || 'sin estatus').replace(/_/g, ' ')}</p>
            </div>
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
              <p className="text-xs text-slate-500">Total</p>
              <p className="font-semibold text-slate-800 mt-1">{fmtMonto(cotizacion.total || cotizacion.total_usd || 0, cotizacion.moneda || 'USD')}</p>
            </div>
          </div>

          <div className="border border-slate-200 rounded-xl overflow-hidden">
            <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 font-semibold text-slate-700">Artículos y servicios</div>
            {articulos.length === 0 ? (
              <p className="p-5 text-sm text-slate-500">Esta cotización no tiene artículos disponibles.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase text-slate-500 border-b border-slate-200">
                      <th className="px-4 py-3">Descripción</th>
                      <th className="px-4 py-3 text-right">Cantidad</th>
                      <th className="px-4 py-3 text-right">Precio unitario</th>
                      <th className="px-4 py-3 text-right">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {articulos.map((articulo, index) => {
                      const cantidad = Number(articulo.cantidad) || 0;
                      const precio = Number(articulo.precio_venta ?? articulo.precio_unitario) || 0;
                      return (
                        <tr key={articulo.id || index}>
                          <td className="px-4 py-3 text-slate-700">{articulo.nombre || articulo.articulo_nombre || articulo.descripcion || 'Artículo'}</td>
                          <td className="px-4 py-3 text-right text-slate-600">{cantidad}</td>
                          <td className="px-4 py-3 text-right text-slate-600">{fmtMonto(precio, cotizacion.moneda || 'USD')}</td>
                          <td className="px-4 py-3 text-right font-semibold text-slate-700">{fmtMonto(cantidad * precio, cotizacion.moneda || 'USD')}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {cotizacion.notas && (
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800">
              <strong>Notas:</strong> {cotizacion.notas}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FacturasView;
